import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { FloatingHScrollSlider } from '../components/ui/FloatingHScrollSlider';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuCheckboxItem } from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { PhoneInput } from '../components/ui/PhoneInput';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, MoreHorizontal, Eye, Trash2, Loader2, ChevronLeft, ChevronRight, Filter, X, ArrowRightLeft, UserCircle, Upload, FileText, Sparkles, MessageCircle, Phone, Mail, MessageSquare, ClipboardCheck, Clock, ArrowRight, History, Columns, SlidersHorizontal, RotateCcw, ChevronDown, Pencil, Check, FolderInput, Coins, Archive, User, Users, Send, FileX, FileQuestion, Forward, Inbox, Bell } from 'lucide-react';
import { toast } from 'sonner';
import CSVImportModal from '../components/CSVImportModal';
import CSVExportButton from '../components/CSVExportButton';
import ApplicationChat from '../components/ApplicationChat';
import ReminderDialog from '../components/ReminderDialog';
import ContentWriterDialog from '../components/ContentWriterDialog';
import { ApplicationTableRow } from '../components/ui/ApplicationTableRow';
import DuplicateApplicationDialog from '../components/DuplicateApplicationDialog';
import { QueryBuilder } from '../components/ui/QueryBuilder';
import CreditCostBadge, { CREDIT_FEATURES } from '../components/CreditCostBadge';
import { triggerCreditUpdate } from '../components/CreditIndicator';

// All available columns configuration
const ALL_COLUMNS = [
  { id: 'checkbox', label: 'Select', required: true, width: '50px' },
  { id: 'app_number', label: 'App #', sortable: true, width: '90px' },
  { id: 'student_name', label: 'Student Name', sortable: true },
  { id: 'student_email', label: 'Email', sortable: false },
  { id: 'student_phone', label: 'Phone', sortable: false },
  { id: 'country', label: 'Country', sortable: true },
  { id: 'partner_university', label: 'Partner University', sortable: true },
  { id: 'app_type', label: 'Type', sortable: false },
  { id: 'assigned_to', label: 'Case Owner', sortable: true },
  { id: 'current_desk', label: 'Current Desk', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'documents', label: 'Docs', sortable: false },
  { id: 'reminders', label: 'Reminders', sortable: false },
  { id: 'created_at', label: 'Created', sortable: true },
  { id: 'app_age', label: 'App Age', sortable: true },
  { id: 'offer_age', label: 'Offer Age', sortable: true },
  { id: 'apply_date', label: 'Apply Date to Uni', sortable: true },
  { id: 'offer_date', label: 'Date of Offer', sortable: true },
  { id: 'actions', label: 'Actions', required: true, width: '100px' },
  { id: 'more', label: '', required: true, width: '50px' },
];

// Default visible columns
const DEFAULT_COLUMNS = ['checkbox', 'app_number', 'student_name', 'student_email', 'country', 'partner_university', 'app_type', 'assigned_to', 'current_desk', 'status', 'documents', 'reminders', 'created_at', 'app_age', 'offer_age', 'apply_date', 'offer_date', 'actions', 'more'];

// Load saved columns from localStorage
const getSavedColumns = () => {
  try {
    const saved = localStorage.getItem('applications_visible_columns');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure required columns are always present
      const required = ['checkbox', 'actions', 'more'];
      required.forEach(col => {
        if (!parsed.includes(col)) parsed.push(col);
      });
      // Add new columns that might be missing from old saved preferences
      const newColumns = ['reminders'];
      newColumns.forEach(col => {
        if (!parsed.includes(col)) {
          // Insert reminders after documents column
          const docsIndex = parsed.indexOf('documents');
          if (docsIndex !== -1) {
            parsed.splice(docsIndex + 1, 0, col);
          } else {
            parsed.push(col);
          }
        }
      });
      // r54 — App # column. Insert just after the checkbox.
      if (!parsed.includes('app_number')) {
        const checkboxIdx = parsed.indexOf('checkbox');
        if (checkboxIdx !== -1) parsed.splice(checkboxIdx + 1, 0, 'app_number');
        else parsed.unshift('app_number');
      }
      // r57 — Apply Date / Offer Date columns inserted after offer_age, before actions.
      const insertAfter = (col, beforeWho) => {
        if (parsed.includes(col)) return;
        const i = parsed.indexOf(beforeWho);
        if (i !== -1) parsed.splice(i + 1, 0, col);
        else parsed.push(col);
      };
      // Remove from older positions if a previous version put them after status
      const removeIf = (col) => {
        const i = parsed.indexOf(col);
        if (i !== -1) parsed.splice(i, 1);
      };
      removeIf('apply_date');
      removeIf('offer_date');
      insertAfter('apply_date', 'offer_age');
      insertAfter('offer_date', 'apply_date');
      return parsed;
    }
  } catch (e) {
    // Ignore parse errors
  }
  return DEFAULT_COLUMNS;
};

// Helper function to render select items (outside component to avoid babel issue)
const renderSelectItems = (items, valueKey, labelKey) => {
  if (!items || !items.length) return null;
  const result = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    result.push(
      <SelectItem key={item[valueKey] || item} value={item[valueKey] || item}>
        {item[labelKey] || item}
      </SelectItem>
    );
  }
  return result;
};

// Helper function to render column checkbox items (outside component)
const renderColumnCheckboxItems = (columns, visibleColumns, toggleColumn) => {
  const result = [];
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    if (col.required) continue;
    result.push(
      <DropdownMenuCheckboxItem
        key={col.id}
        checked={visibleColumns.includes(col.id)}
        onCheckedChange={() => toggleColumn(col.id)}
      >
        {col.label}
      </DropdownMenuCheckboxItem>
    );
  }
  return result;
};

const ApplicationsPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deskFilter, setDeskFilter] = useState('all');
  
  // Initialize page from URL params or default to 1
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const [page, setPage] = useState(initialPage);
  
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [desks, setDesks] = useState([]);
  const [applicationStatuses, setApplicationStatuses] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [bulkShiftOpen, setBulkShiftOpen] = useState(false);
  const [bulkTargetDesk, setBulkTargetDesk] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkShifting, setBulkShifting] = useState(false);
  
  // Global Desk Shifting dialog state
  const [globalDeskShiftOpen, setGlobalDeskShiftOpen] = useState(false);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    student_name: '',
    student_email: '',
    student_phone: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    passport_number: '',
    passport_issue_date: '',
    passport_expiry_date: '',
    // NIC fields
    nic_number: '',
    nic_issue_date: '',
    nic_expiry_date: '',
    address: '',
    city: '',
    province: ''
  });
  const [users, setUsers] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningApp, setAssigningApp] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [documentType, setDocumentType] = useState('passport');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatApplication, setChatApplication] = useState(null);
  const [agentChatOpen, setAgentChatOpen] = useState(false);
  const [agentChatApplication, setAgentChatApplication] = useState(null);
  const [agentChatMessages, setAgentChatMessages] = useState([]);
  const [agentChatNewMessage, setAgentChatNewMessage] = useState('');
  const [agentChatSending, setAgentChatSending] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderApplication, setReminderApplication] = useState(null);
  const [docShortfallOpen, setDocShortfallOpen] = useState(false);
  const [docShortfallApp, setDocShortfallApp] = useState(null);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadedDocTypes, setUploadedDocTypes] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [missingFields, setMissingFields] = useState({ nationality: false, degreeType: false });
  const [docDialogNationality, setDocDialogNationality] = useState('');
  const [docDialogDegreeType, setDocDialogDegreeType] = useState('');
  const [savingDocFields, setSavingDocFields] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailApp, setEmailApp] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsApp, setSmsApp] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSMS, setSendingSMS] = useState(false);
  // WhatsApp compose (via Chrome extension queue)
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waApp, setWaApp] = useState(null);
  const [waMessage, setWaMessage] = useState('');
  const [waPhone, setWaPhone] = useState('');
  const [sendingWA, setSendingWA] = useState(false);
  // Content Writer (SOP / Resume)
  const [contentWriterOpen, setContentWriterOpen] = useState(false);
  const [contentWriterApp, setContentWriterApp] = useState(null);
  
  // Activity Log
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [activityLogApp, setActivityLogApp] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivityLog, setLoadingActivityLog] = useState(false);
  const [vettingDialogOpen, setVettingDialogOpen] = useState(false);
  const [vettingApp, setVettingApp] = useState(null);
  const [vettingReport, setVettingReport] = useState(null);
  const [isVetting, setIsVetting] = useState(false);
  
  // Reminder stats per application (sent and pending counts)
  const [reminderStats, setReminderStats] = useState({});
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Desk History Dialog State
  const [deskHistoryOpen, setDeskHistoryOpen] = useState(false);
  const [deskHistoryLoading, setDeskHistoryLoading] = useState(false);
  const [deskHistoryData, setDeskHistoryData] = useState(null);

  // r66.8 — Duplicate Application Dialog State
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const openDuplicateDialog = (app) => {
    setDuplicateSource(app);
    setDuplicateOpen(true);
  };
  
  // Column visibility state - load from localStorage
  const [visibleColumns, setVisibleColumns] = useState(() => getSavedColumns());
  
  // Save columns to localStorage whenever they change
  const updateVisibleColumns = (newColumns) => {
    setVisibleColumns(newColumns);
    try {
      localStorage.setItem('applications_visible_columns', JSON.stringify(newColumns));
    } catch (e) {
      // Ignore storage errors
    }
  };
  
  // Records per page state - Initialize from URL params
  const initialRecordsPerPage = parseInt(searchParams.get('limit')) || 20;
  const [recordsPerPage, setRecordsPerPage] = useState(initialRecordsPerPage);
  
  // Query Builder state
  const [showQueryBuilder, setShowQueryBuilder] = useState(false);
  const [activeQuery, setActiveQuery] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  
  // Advanced Filters state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    country: 'all',
    assignedTo: 'all',
    appType: 'all',
    agentId: 'all',
    dateFrom: '',
    dateTo: '',
    ageMin: '',
    ageMax: ''
  });
  
  // Application Types and Agents for filters
  const [applicationTypes, setApplicationTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Application Tab state - persist in URL params for back button support
  const [activeTab, setActiveTabState] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) return tabFromUrl;
    // Team Admin default lands on their team-scoped list
    const roles = user?.roles || [];
    if (roles.some(r => (r.name || '').toLowerCase() === 'team admin')) return 'team_applications';
    return 'leads_no_doc';
  });
  
  // Desk view filter from navigation (my, admission_coordinator, admission_officer)
  const [deskView, setDeskView] = useState(() => {
    return searchParams.get('view') || null;
  });
  
  // Update URL when tab changes
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    // Clear view param when manually selecting a tab
    newParams.delete('view');
    setSearchParams(newParams, { replace: true });
    setDeskView(null);
  };
  
  // Sync tab and view from URL on mount/navigation
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const viewFromUrl = searchParams.get('view');
    
    if (viewFromUrl) {
      setDeskView(viewFromUrl);
      // When view is set, default to showing all applications in that view
      if (!tabFromUrl) {
        setActiveTabState('all');
      }
    } else {
      setDeskView(null);
    }
    
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTabState(tabFromUrl);
    }
  }, [searchParams]);
  
  const [tabCounts, setTabCounts] = useState({ 
    leads_no_doc: 0, 
    leads_incomplete_doc: 0, 
    forwarded_cases: 0, 
    pending_review: 0,
    unassigned_leads: 0,
    all_applications: 0
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [isAdmissionRole, setIsAdmissionRole] = useState(false);
  const [duplicates, setDuplicates] = useState({});
  const [customTabs, setCustomTabs] = useState({}); // Custom tabs with counts
  
  // Check if user is Super Admin
  const isSuperAdmin = user?.is_super_admin || user?.user_type === 'super_admin' || user?.roles?.some(r => r.name === 'Super Admin');
  const isTeamAdmin = user?.roles?.some(r => (r.name || '').toLowerCase() === 'team admin');
  
  // Set correct default tab for Admin & Super Admin (single "All Applications" tab)
  useEffect(() => {
    if ((isSuperAdmin || isAdmin) && activeTab !== 'all_applications') {
      setActiveTabState('all_applications');
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'all_applications');
      setSearchParams(newParams, { replace: true });
    }
  }, [isSuperAdmin, isAdmin]);
  
  // Check if user is Admission Officer (for hiding Fresh Leads tab)
  const isAdmissionOfficer = userRoles.some(role => 
    role.toLowerCase().includes('admission') && role.toLowerCase().includes('officer')
  );
  
  // Check if user is Admission Coordinator or Officer (for showing Pending Review tab)
  const isAdmissionCoordinatorOrOfficer = userRoles.some(role => {
    const r = role.toLowerCase();
    return r.includes('admission') && (r.includes('coordinator') || r.includes('officer'));
  });
  
  // Read/Unread tracking for applications
  const [readApplications, setReadApplications] = useState(() => {
    const stored = localStorage.getItem('readApplications');
    return stored ? JSON.parse(stored) : [];
  });
  
  // Save read applications to localStorage
  useEffect(() => {
    localStorage.setItem('readApplications', JSON.stringify(readApplications));
  }, [readApplications]);
  
  // Mark application as read
  const markAppAsRead = (appId) => {
    if (!readApplications.includes(appId)) {
      setReadApplications(prev => [...prev, appId]);
    }
  };
  
  // Check if application is unread
  const isAppUnread = (appId) => !readApplications.includes(appId);
  const [tabNames, setTabNames] = useState({
    tab1_name: 'Fresh Leads',
    tab2_name: 'Active Applications',
    tab3_name: 'My Cases',
    tab4_name: 'Unassigned Leads'
  });
  const [editingTabName, setEditingTabName] = useState(null);
  const [tempTabName, setTempTabName] = useState('');
  const [tabShiftOpen, setTabShiftOpen] = useState(false);
  const [tabShiftTarget, setTabShiftTarget] = useState('');
  const [tabShiftNotes, setTabShiftNotes] = useState('');
  const [tabShifting, setTabShifting] = useState(false);
  const [newCustomTabName, setNewCustomTabName] = useState(''); // For creating new custom tabs
  
  // Create Tab dialog state
  const [createTabOpen, setCreateTabOpen] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [newTabColor, setNewTabColor] = useState('#f97316'); // Default orange
  const [creatingTab, setCreatingTab] = useState(false);
  
  // Available colors for custom tabs
  const tabColors = [
    { name: 'Orange', value: '#f97316' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Cyan', value: '#06b6d4' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Gray', value: '#6b7280' },
  ];
  
  // Custom tab colors stored in state (fetched from backend)
  const [customTabColors, setCustomTabColors] = useState({});
  
  const fileInputRef = useRef(null);

  // Fetch tab counts
  const fetchTabCounts = async () => {
    try {
      const response = await api().get('/applications/tab-counts');
      setTabCounts({
        leads_no_doc: response.data.leads_no_doc || 0,
        leads_incomplete_doc: response.data.leads_incomplete_doc || 0,
        forwarded_cases: response.data.forwarded_cases || 0,
        pending_review: response.data.pending_review || 0,
        unassigned_leads: response.data.unassigned_leads || 0,
        all_applications: response.data.all_applications || 0,
        team_applications: response.data.team_applications || 0,
        // Keep legacy counts for backwards compatibility
        fresh_leads: response.data.fresh_leads || 0,
        active_applications: response.data.active_applications || 0,
        my_cases: response.data.my_cases || 0,
      });
      // Track if user is admin based on backend response
      setIsAdmin(response.data.is_admin || false);
      // Track user roles for role-based visibility
      setUserRoles(response.data.user_roles || []);
      // Track if user has admission role
      setIsAdmissionRole(response.data.is_admission_role || false);
      // Track custom tabs
      setCustomTabs(response.data.custom_tabs || {});
      // Track custom tab colors
      setCustomTabColors(response.data.custom_tab_colors || {});
    } catch (error) {
      console.error('Failed to load tab counts');
    }
  };
  
  // Fetch duplicate applications
  const fetchDuplicates = async () => {
    try {
      const response = await api().get('/applications/duplicates');
      setDuplicates(response.data.duplicates || {});
    } catch (error) {
      console.error('Failed to load duplicates');
    }
  };

  // Fetch tab preferences
  const fetchTabPreferences = async () => {
    try {
      const response = await api().get('/user-preferences/application-tabs');
      setTabNames(response.data);
    } catch (error) {
      console.error('Failed to load tab preferences');
    }
  };

  // Save tab name
  const saveTabName = async (tabKey) => {
    if (!tempTabName.trim()) {
      setEditingTabName(null);
      return;
    }
    
    const newTabNames = { ...tabNames, [tabKey]: tempTabName.trim() };
    setTabNames(newTabNames);
    setEditingTabName(null);
    
    try {
      await api().put('/user-preferences/application-tabs', newTabNames);
      toast.success('Tab name updated');
    } catch (error) {
      toast.error('Failed to save tab name');
    }
  };

  // Start editing tab name
  const startEditingTab = (tabKey, currentName) => {
    setEditingTabName(tabKey);
    setTempTabName(currentName);
  };

  // Handle tab shift
  const handleTabShift = async () => {
    if (!tabShiftTarget || selectedApps.length === 0) return;
    
    // Determine the actual target tab (handle new custom tab creation)
    let actualTarget = tabShiftTarget;
    if (tabShiftTarget === '__new_custom_tab__') {
      if (!newCustomTabName.trim()) {
        toast.error('Please enter a name for the new tab');
        return;
      }
      actualTarget = newCustomTabName.trim();
    }
    
    setTabShifting(true);
    try {
      await api().post('/applications/tab-shift', {
        application_ids: selectedApps,
        target_tab: actualTarget,
        notes: tabShiftNotes
      });
      const tabNameMap = {
        'fresh_leads': tabNames.tab1_name,
        'active_applications': tabNames.tab2_name,
        'my_cases': tabNames.tab3_name,
        'unassigned_leads': tabNames.tab4_name
      };
      const displayName = tabNameMap[actualTarget] || actualTarget;
      toast.success(`${selectedApps.length} application(s) moved to ${displayName}`);
      setTabShiftOpen(false);
      setSelectedApps([]);
      setTabShiftTarget('');
      setTabShiftNotes('');
      setNewCustomTabName('');
      fetchApplications();
      fetchTabCounts();
    } catch (error) {
      toast.error('Failed to move applications');
    } finally {
      setTabShifting(false);
    }
  };

  // Create new custom tab
  const handleCreateTab = async () => {
    if (!newTabName.trim()) {
      toast.error('Please enter a tab name');
      return;
    }
    
    setCreatingTab(true);
    try {
      await api().post('/applications/custom-tabs', {
        name: newTabName.trim(),
        color: newTabColor
      });
      toast.success(`Tab "${newTabName}" created successfully`);
      setCreateTabOpen(false);
      setNewTabName('');
      setNewTabColor('#f97316');
      fetchTabCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tab');
    } finally {
      setCreatingTab(false);
    }
  };

  // Distribute leads to counselors
  const [distributing, setDistributing] = useState(false);
  const handleDistributeLeads = async () => {
    setDistributing(true);
    try {
      const response = await api().post('/leads/distribute');
      const { distributed, skipped } = response.data;
      if (distributed > 0) {
        toast.success(`${distributed} lead(s) distributed to counselors`);
        fetchApplications();
        fetchTabCounts();
      } else if (skipped > 0) {
        toast.info(`No leads distributed. ${skipped} lead(s) skipped (no matching team or no university assigned)`);
      } else {
        toast.info('No unassigned leads to distribute');
      }
    } catch (error) {
      toast.error('Failed to distribute leads');
    } finally {
      setDistributing(false);
    }
  };

  // Fetch desk history for an application
  const fetchDeskHistory = async (appId, e) => {
    e.stopPropagation();
    setDeskHistoryLoading(true);
    setDeskHistoryOpen(true);
    try {
      const response = await api().get(`/applications/${appId}/desk-history`);
      setDeskHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to load desk history');
      setDeskHistoryOpen(false);
    } finally {
      setDeskHistoryLoading(false);
    }
  };

  // Fetch reminder stats (sent and pending) for visible applications
  const fetchReminderStats = async (appIds) => {
    try {
      const response = await api().post('/reminders/stats/batch', {
        application_ids: appIds
      });
      setReminderStats(response.data || {});
    } catch (error) {
      console.error('Failed to fetch reminder stats:', error);
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchDesks();
    fetchApplicationStatuses();
    fetchUsers();
    fetchUnreadCounts();
    fetchSavedFilters();
    fetchTabCounts();
    fetchDuplicates();
  }, [page, search, statusFilter, deskFilter, sortBy, sortOrder, recordsPerPage, activeQuery, advancedFilters, activeTab]);

  // Update URL when page or recordsPerPage changes (for returning to same page after viewing an application)
  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams);
    if (page > 1) {
      currentParams.set('page', page.toString());
    } else {
      currentParams.delete('page');
    }
    if (recordsPerPage !== 20) {
      currentParams.set('limit', recordsPerPage.toString());
    } else {
      currentParams.delete('limit');
    }
    setSearchParams(currentParams, { replace: true });
  }, [page, recordsPerPage]);

  // Fetch reminder stats when applications change
  useEffect(() => {
    if (applications.length > 0) {
      const appIds = applications.map(app => app.id);
      fetchReminderStats(appIds);
    }
  }, [applications]);

  // Load tab preferences on mount
  useEffect(() => {
    fetchTabPreferences();
  }, []);

  // Poll for unread message counts
  useEffect(() => {
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCounts = async () => {
    try {
      const response = await api().get('/chat/unread-count');
      setUnreadCounts(response.data.by_application || {});
    } catch (error) {
      // Silent fail
    }
  };

  const fetchSavedFilters = async () => {
    try {
      const response = await api().get('/filters/saved?entity_type=applications');
      setSavedFilters(response.data);
    } catch (error) {
      // Silent fail
    }
  };

  const handleQueryApply = async (query) => {
    setActiveQuery(query);
    setPage(1);
    
    if (!query || !query.groups || query.groups.length === 0) {
      // No query, use regular fetch
      fetchApplications();
      return;
    }
    
    // Use advanced filter endpoint
    setLoading(true);
    try {
      const response = await api().post('/applications/filter', query, {
        params: { page: 1, limit: recordsPerPage, sort_by: sortBy, sort_order: sortOrder }
      });
      setApplications(response.data.items);
      setTotalPages(response.data.pages);
      setTotalRecords(response.data.total);
    } catch (error) {
      toast.error('Failed to apply filter');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFilter = async (filterData) => {
    try {
      await api().post('/filters/saved', {
        name: filterData.name,
        query: filterData.query,
        entity_type: 'applications'
      });
      toast.success('Filter saved!');
      fetchSavedFilters();
    } catch (error) {
      toast.error('Failed to save filter');
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await api().delete(`/filters/saved/${filterId}`);
      toast.success('Filter deleted');
      fetchSavedFilters();
    } catch (error) {
      toast.error('Failed to delete filter');
    }
  };

  const openChat = (app) => {
    setChatApplication(app);
    setChatOpen(true);
  };

  // Open agent chat for agent-submitted applications
  const openAgentChat = (app) => {
    if (app.agent_id) {
      setAgentChatApplication(app);
      setAgentChatOpen(true);
      fetchAgentChatMessages(app.id);
    }
  };

  // Fetch agent chat messages
  const fetchAgentChatMessages = async (appId) => {
    try {
      const response = await api().get(`/applications/${appId}/agent-chat`);
      // API returns {messages: [...], agent: {...}}
      const data = response.data;
      if (Array.isArray(data)) {
        setAgentChatMessages(data);
      } else if (data && Array.isArray(data.messages)) {
        setAgentChatMessages(data.messages);
      } else {
        setAgentChatMessages([]);
      }
    } catch (error) {
      console.error('Failed to fetch agent chat:', error);
      setAgentChatMessages([]);
    }
  };

  // Send message to agent
  const sendAgentChatMessage = async () => {
    if (!agentChatNewMessage.trim() || !agentChatApplication) return;
    
    setAgentChatSending(true);
    try {
      await api().post(`/applications/${agentChatApplication.id}/agent-chat`, {
        message: agentChatNewMessage
      });
      setAgentChatNewMessage('');
      fetchAgentChatMessages(agentChatApplication.id);
      toast.success('Message sent to agent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setAgentChatSending(false);
    }
  };

  const openReminder = (app) => {
    setReminderApplication(app);
    setReminderOpen(true);
  };

  const openActivityLog = async (app) => {
    setActivityLogApp(app);
    setActivityLogOpen(true);
    setLoadingActivityLog(true);
    
    try {
      const response = await api().get(`/applications/${app.id}/activity-log`);
      setActivityLogs(response.data || []);
    } catch (error) {
      toast.error('Failed to load activity log');
      setActivityLogs([]);
    } finally {
      setLoadingActivityLog(false);
    }
  };

  const openDocShortfall = async (app, e) => {
    e.stopPropagation();
    setDocShortfallApp(app);
    setDocShortfallOpen(true);
    setLoadingDocs(true);
    setMissingFields({ nationality: false, degreeType: false });
    setDocDialogNationality('');
    setDocDialogDegreeType('');
    
    try {
      // Fetch required documents for this university - considering country and degree type
      const universityId = app.university_id || 
                          app.desk_data?.admission?.partner_university || 
                          app.desk_data?.admission?.university;
      
      // Check what fields are missing
      const nationalityId = app.desk_data?.counselor?.nationality || app.desk_data?.counselor?.country_living;
      const degreeTypeId = app.desk_data?.admission?.degree_type;
      
      const fieldsMissing = {
        nationality: !nationalityId,
        degreeType: !degreeTypeId
      };
      setMissingFields(fieldsMissing);
      
      let reqDocs = [];
      if (universityId) {
        let countryName = null;
        if (nationalityId) {
          try {
            const countryRes = await api().get(`/master-data/${nationalityId}`);
            countryName = countryRes.data?.name;
          } catch (err) {
            console.log('Could not resolve nationality/country_living to country name');
          }
        }
        
        // Get degree type name
        let degreeTypeName = null;
        if (degreeTypeId) {
          try {
            // Check if it looks like a UUID
            if (degreeTypeId.length === 36 && degreeTypeId.includes('-')) {
              const degreeTypeRes = await api().get(`/master-data/${degreeTypeId}`);
              degreeTypeName = degreeTypeRes.data?.name;
            } else {
              degreeTypeName = degreeTypeId; // Already a name
            }
          } catch (err) {
            console.log('Could not resolve degree type');
          }
        }
        
        // Fetch required docs with country and degree type
        if (countryName && degreeTypeName) {
          try {
            const reqResponse = await api().get(
              `/university-required-docs/${universityId}/country/${countryName}?degree_type=${encodeURIComponent(degreeTypeName)}`
            );
            reqDocs = reqResponse.data.required_documents || [];
          } catch (err) {
            console.log('Could not fetch degree-specific docs');
          }
        }
        
        // Fallback 1: If no docs found with degree type, try just country
        if (reqDocs.length === 0 && countryName) {
          try {
            const reqResponse = await api().get(
              `/university-required-docs/${universityId}/country/${countryName}`
            );
            reqDocs = reqResponse.data.required_documents || [];
          } catch (err) {
            console.log('Could not fetch country-specific docs');
          }
        }
        
        // Fallback 2: Try university-level docs if still nothing found
        if (reqDocs.length === 0 && !fieldsMissing.nationality && !fieldsMissing.degreeType) {
          try {
            const reqResponse = await api().get(`/university-required-docs/${universityId}`);
            reqDocs = reqResponse.data.required_documents || [];
          } catch (err) {
            console.log('No required docs found');
          }
        }
      }
      setRequiredDocs(reqDocs);
      
      // Fetch uploaded documents for this application
      const docsResponse = await api().get(`/documents?application_id=${app.id}`);
      const docs = docsResponse.data || [];
      setUploadedDocs(docs);
      
      // Fetch document types to get type names for matching
      const docTypesResponse = await api().get('/master-data?type=document_type');
      const docTypes = docTypesResponse.data || [];
      
      // Map uploaded docs to their type names
      // Check both document_type_id lookup AND document_type_name field
      const uploadedTypeNames = docs.map(doc => {
        // First try to get name from document_type_name field directly
        if (doc.document_type_name) {
          return doc.document_type_name.toLowerCase();
        }
        // Then try lookup by document_type_id
        if (doc.document_type_id) {
          const docType = docTypes.find(dt => dt.id === doc.document_type_id);
          if (docType) return docType.name.toLowerCase();
        }
        // Also check document_type field (used by some sources)
        if (doc.document_type) {
          return doc.document_type.toLowerCase();
        }
        return '';
      }).filter(name => name);
      
      setUploadedDocTypes(uploadedTypeNames);
      console.log('Required:', reqDocs, 'Uploaded types:', uploadedTypeNames, 'Uploaded docs:', docs.length, 'Missing fields:', fieldsMissing);
    } catch (error) {
      console.error('Error fetching docs:', error);
      toast.error('Failed to load document information');
      setRequiredDocs([]);
      setUploadedDocs([]);
      setUploadedDocTypes([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Save missing fields (nationality/degree type) and refresh document list
  const saveDocDialogFields = async () => {
    if (!docShortfallApp) return;
    
    setSavingDocFields(true);
    try {
      // Prepare update payload
      const updateData = {
        desk_data: { ...docShortfallApp.desk_data }
      };
      
      // Initialize nested objects if they don't exist
      if (!updateData.desk_data.counselor) updateData.desk_data.counselor = {};
      if (!updateData.desk_data.admission) updateData.desk_data.admission = {};
      
      // Update nationality if provided
      if (docDialogNationality && missingFields.nationality) {
        updateData.desk_data.counselor.nationality = docDialogNationality;
      }
      
      // Update degree type if provided
      if (docDialogDegreeType && missingFields.degreeType) {
        updateData.desk_data.admission.degree_type = docDialogDegreeType;
      }
      
      // Save to backend
      await api().put(`/applications/${docShortfallApp.id}`, updateData);
      
      // Update local app object
      const updatedApp = {
        ...docShortfallApp,
        desk_data: updateData.desk_data
      };
      setDocShortfallApp(updatedApp);
      
      toast.success('Application updated successfully');
      
      // Re-fetch required docs with new data
      await openDocShortfall(updatedApp, { stopPropagation: () => {} });
      
      // Refresh the applications list to update the Docs column
      fetchApplications();
    } catch (error) {
      console.error('Error saving fields:', error);
      toast.error('Failed to save fields');
    } finally {
      setSavingDocFields(false);
    }
  };

  const getMissingDocs = () => {
    if (!requiredDocs.length) return [];
    
    // Get all uploaded document info for matching
    const uploadedFilenames = uploadedDocs.map(d => {
      const name = (d.file_name || d.filename || '').toLowerCase();
      return name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ').trim();
    });
    
    // Also get document_type from uploaded docs for matching
    const uploadedDocTypeNames = uploadedDocs.map(d => 
      (d.document_type || '').toLowerCase().trim()
    ).filter(Boolean);
    
    return requiredDocs.filter(reqDoc => {
      const reqNormalized = reqDoc.toLowerCase().trim();
      const reqWords = reqNormalized.split(/[\s\/]+/).filter(w => w.length > 2);
      
      // 1. Check if uploaded doc type matches required doc name (from uploadedDocTypes state)
      const typeMatch = uploadedDocTypes.some(typeName => {
        if (!typeName) return false;
        const typeNorm = typeName.toLowerCase().trim();
        // Exact match
        if (typeNorm === reqNormalized) return true;
        // Partial match
        if (typeNorm.includes(reqNormalized) || reqNormalized.includes(typeNorm)) return true;
        // Word-based matching for things like "IELTS Result" vs "English Test"
        const typeWords = typeNorm.split(/[\s\/]+/).filter(w => w.length > 2);
        
        // Check for common document keywords - comprehensive matching
        const ieltsKeywords = ['ielts', 'toefl', 'english', 'test', 'pte', 'language', 'oxford', 'proficiency', 'score'];
        const transcriptKeywords = ['transcript', 'degree', 'bachelor', 'master', 'diploma', 'certificate', 'marksheet', 'last'];
        const passportKeywords = ['passport', 'travel'];
        const resumeKeywords = ['resume', 'cv', 'curriculum', 'vitae'];
        const experienceKeywords = ['experience', 'work', 'employment', 'job', 'letter', 'certificate', 'employer'];
        const nicKeywords = ['nic', 'national', 'cnic', 'identity', 'ni', 'card'];
        
        // IELTS/English Test matching
        if (ieltsKeywords.some(k => reqNormalized.includes(k)) && 
            ieltsKeywords.some(k => typeNorm.includes(k))) return true;
        // Transcript/Degree matching
        if (transcriptKeywords.some(k => reqNormalized.includes(k)) && 
            transcriptKeywords.some(k => typeNorm.includes(k))) return true;
        // Passport matching
        if (passportKeywords.some(k => reqNormalized.includes(k)) && 
            passportKeywords.some(k => typeNorm.includes(k))) return true;
        // Resume/CV matching
        if (resumeKeywords.some(k => reqNormalized.includes(k)) && 
            resumeKeywords.some(k => typeNorm.includes(k))) return true;
        // Experience letter/certificate matching
        if (experienceKeywords.some(k => reqNormalized.includes(k)) && 
            experienceKeywords.some(k => typeNorm.includes(k))) return true;
        // NIC/National ID matching
        if (nicKeywords.some(k => reqNormalized.includes(k)) && 
            nicKeywords.some(k => typeNorm.includes(k))) return true;
        
        // Check word overlap - if any significant words match
        const matchingWords = reqWords.filter(w => typeWords.includes(w) || typeNorm.includes(w));
        if (matchingWords.length > 0) return true;
        
        return false;
      });
      if (typeMatch) return false; // Document is uploaded
      
      // 2. Check uploaded doc's document_type field directly
      const docTypeFieldMatch = uploadedDocTypeNames.some(typeName => {
        if (!typeName) return false;
        if (typeName === reqNormalized) return true;
        if (typeName.includes(reqNormalized) || reqNormalized.includes(typeName)) return true;
        // Comprehensive keyword matching
        const ieltsKeywords = ['ielts', 'toefl', 'english', 'test', 'pte', 'oxford', 'proficiency', 'score'];
        const transcriptKeywords = ['transcript', 'degree', 'bachelor', 'master', 'certificate', 'marksheet', 'last'];
        const experienceKeywords = ['experience', 'work', 'employment', 'letter', 'certificate', 'employer'];
        const resumeKeywords = ['resume', 'cv', 'curriculum', 'vitae'];
        const nicKeywords = ['nic', 'ni', 'national', 'cnic', 'identity'];
        
        if (ieltsKeywords.some(k => reqNormalized.includes(k)) && 
            ieltsKeywords.some(k => typeName.includes(k))) return true;
        if (transcriptKeywords.some(k => reqNormalized.includes(k)) && 
            transcriptKeywords.some(k => typeName.includes(k))) return true;
        if (experienceKeywords.some(k => reqNormalized.includes(k)) && 
            experienceKeywords.some(k => typeName.includes(k))) return true;
        if (resumeKeywords.some(k => reqNormalized.includes(k)) && 
            resumeKeywords.some(k => typeName.includes(k))) return true;
        if (nicKeywords.some(k => reqNormalized.includes(k)) && 
            nicKeywords.some(k => typeName.includes(k))) return true;
        return false;
      });
      if (docTypeFieldMatch) return false;
      
      // 3. Check if any uploaded filename matches required doc name
      const filenameMatch = uploadedFilenames.some(filename => {
        if (filename === reqNormalized) return true;
        if (filename.includes(reqNormalized)) return true;
        if (reqNormalized.includes(filename) && filename.length > 3) return true;
        // Word match
        return reqWords.some(word => word.length > 3 && filename.includes(word));
      });
      if (filenameMatch) return false; // Document is uploaded
      
      return true; // Document is missing
    });
  };

  const sendDocShortfallWhatsApp = () => {
    if (!docShortfallApp) return;
    
    const phone = docShortfallApp.student_phone?.replace(/[^0-9]/g, '');
    if (!phone) {
      toast.error('No phone number available for this student');
      return;
    }
    
    const missingDocs = getMissingDocs();
    if (missingDocs.length === 0) {
      toast.info('All required documents have been submitted');
      return;
    }
    
    const studentName = docShortfallApp.student_name || 'Student';
    const universityName = docShortfallApp.university_name || 'the university';
    
    const message = `Hi ${studentName},

Your application for ${universityName} is missing the following documents:

${missingDocs.map((doc, i) => `${i + 1}. ${doc}`).join('\n')}

Please submit these documents at your earliest convenience to avoid delays in processing your application.

Thank you.`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
    setDocShortfallOpen(false);
  };

  const openEmailCompose = async (app, e) => {
    e.stopPropagation();
    if (!app.student_email) {
      toast.error('No email address available for this student');
      return;
    }
    setEmailApp(app);
    setEmailForm({ subject: '', body: '' });
    setEmailDialogOpen(true);
    
    // Fetch email templates
    try {
      const response = await api().get('/email-templates');
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates');
    }
  };

  const applyEmailTemplate = (template) => {
    if (!template) return;
    
    // Replace placeholders with actual values
    let subject = template.subject;
    let body = template.body;
    
    if (emailApp) {
      subject = subject.replace(/\{\{student_name\}\}/g, emailApp.student_name || '');
      subject = subject.replace(/\{\{university_name\}\}/g, emailApp.university_name || '');
      body = body.replace(/\{\{student_name\}\}/g, emailApp.student_name || '');
      body = body.replace(/\{\{university_name\}\}/g, emailApp.university_name || '');
    }
    
    setEmailForm({ subject, body });
  };

  const sendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) {
      toast.error('Please fill in subject and body');
      return;
    }
    
    setSendingEmail(true);
    try {
      await api().post('/send-email', {
        to_email: emailApp.student_email,
        subject: emailForm.subject,
        body: emailForm.body,
        application_id: emailApp.id
      });
      toast.success('Email sent successfully');
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const openSMSCompose = (app, e) => {
    e.stopPropagation();
    if (!app.student_phone) {
      toast.error('No phone number available for this student');
      return;
    }
    setSmsApp(app);
    setSmsMessage('');
    setSmsDialogOpen(true);
  };

  const sendSMS = async () => {
    if (!smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingSMS(true);
    try {
      await api().post('/send-sms', {
        to_phone: smsApp.student_phone,
        message: smsMessage,
        application_id: smsApp.id
      });
      toast.success('SMS sent successfully');
      setSmsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSMS(false);
    }
  };

  // ---- WhatsApp compose via Chrome extension queue ----
  const openWhatsAppCompose = (app, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    const phone = app.student_phone || app.student_phone_2 || '';
    setWaApp(app);
    setWaPhone(phone);
    setWaMessage('');
    setWaDialogOpen(true);
  };

  const sendWhatsApp = async () => {
    if (!waMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    const cleanPhone = (waPhone || '').trim();
    if (!cleanPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setSendingWA(true);
    try {
      await api().post('/whatsapp/send-message', {
        phone: cleanPhone,
        contact_name: waApp?.student_name || '',
        message: waMessage,
        message_type: 'text',
        application_id: waApp?.id || null,
      });
      toast.success('WhatsApp message queued — your extension will send it shortly');
      setWaDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue WhatsApp message');
    } finally {
      setSendingWA(false);
    }
  };

  // ---- Content Writer (SOP / Resume) ----
  const openContentWriter = (app, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setContentWriterApp(app);
    setContentWriterOpen(true);
  };

  const [pastVettingReports, setPastVettingReports] = useState([]);
  const [loadingPastReports, setLoadingPastReports] = useState(false);
  const [vettingEstimate, setVettingEstimate] = useState(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);

  const openVettingDialog = async (app, e) => {
    e.stopPropagation();
    setVettingApp(app);
    setVettingReport(null);
    setPastVettingReports([]);
    setVettingEstimate(null);
    setVettingDialogOpen(true);
    
    // Fetch past vetting reports if application has been vetted before
    if (app.has_vetting_report) {
      setLoadingPastReports(true);
      try {
        const response = await api().get(`/applications/${app.id}/vetting-reports`);
        setPastVettingReports(response.data || []);
        // Auto-load the latest report
        if (response.data && response.data.length > 0) {
          setVettingReport(response.data[0]);
        }
      } catch (error) {
        console.error('Failed to load past reports:', error);
      } finally {
        setLoadingPastReports(false);
      }
    } else {
      // Fetch cost estimate for new vetting
      fetchVettingEstimate(app.id);
    }
  };

  const fetchVettingEstimate = async (appId) => {
    setLoadingEstimate(true);
    try {
      const response = await api().get(`/applications/${appId}/vet-documents/estimate`);
      setVettingEstimate(response.data);
    } catch (error) {
      console.error('Failed to get vetting estimate:', error);
    } finally {
      setLoadingEstimate(false);
    }
  };

  const runDocumentVetting = async () => {
    if (!vettingApp) return;
    
    // Check credits before vetting
    try {
      const creditRes = await api().get('/credits/balance');
      const balance = creditRes.data?.credits?.balance || 0;
      if (balance < 1) {
        toast.error(
          <div className="space-y-1">
            <p className="font-semibold">Out of Credits!</p>
            <p className="text-sm">You need at least 1 credit to run document vetting. Each vetting costs $0.20.</p>
            <button 
              onClick={() => window.location.href = '/billing'} 
              className="text-sm underline text-primary"
            >
              Top up credits →
            </button>
          </div>,
          { duration: 5000 }
        );
        return;
      }
    } catch (error) {
      console.error('Failed to check credits:', error);
    }
    
    setIsVetting(true);
    try {
      const response = await api().post(`/applications/${vettingApp.id}/vet-documents`);
      setVettingReport(response.data);
      // Add to past reports list
      setPastVettingReports(prev => [response.data, ...prev]);
      
      // Trigger credit update in the header
      triggerCreditUpdate();
      
      toast.success('Document vetting completed (1 credit deducted)');
      // Refresh applications to update the vetting indicator
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vet documents');
    } finally {
      setIsVetting(false);
    }
  };

  const fetchApplications = async () => {
    // If there's an active query, use the filter endpoint
    if (activeQuery && activeQuery.groups && activeQuery.groups.length > 0) {
      setLoading(true);
      try {
        const response = await api().post('/applications/filter', activeQuery, {
          params: { page, limit: recordsPerPage, sort_by: sortBy, sort_order: sortOrder }
        });
        setApplications(response.data.items);
        setTotalPages(response.data.pages);
        setTotalRecords(response.data.total);
      } catch (error) {
        toast.error('Failed to load applications');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: recordsPerPage });
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (deskFilter && deskFilter !== 'all') params.append('current_desk', deskFilter);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      
      // Tab filter
      if (activeTab) params.append('tab_filter', activeTab);
      
      // Desk view filter from navigation (my, admission_coordinator, admission_officer)
      if (deskView) params.append('desk_view', deskView);
      
      // Advanced filters
      if (advancedFilters.country && advancedFilters.country !== 'all') params.append('country', advancedFilters.country);
      if (advancedFilters.assignedTo && advancedFilters.assignedTo !== 'all') params.append('assigned_to', advancedFilters.assignedTo);
      if (advancedFilters.appType && advancedFilters.appType !== 'all') params.append('app_type', advancedFilters.appType);
      if (advancedFilters.agentId && advancedFilters.agentId !== 'all') params.append('agent_id', advancedFilters.agentId);
      if (advancedFilters.dateFrom) params.append('date_from', advancedFilters.dateFrom);
      if (advancedFilters.dateTo) params.append('date_to', advancedFilters.dateTo);
      if (advancedFilters.ageMin) params.append('age_min', advancedFilters.ageMin);
      if (advancedFilters.ageMax) params.append('age_max', advancedFilters.ageMax);
      
      const response = await api().get(`/applications?${params}`);
      setApplications(response.data.items);
      setTotalPages(response.data.pages);
      setTotalRecords(response.data.total);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    const column = ALL_COLUMNS.find(c => c.id === columnId);
    if (column?.required) return; // Can't toggle required columns
    
    const newColumns = visibleColumns.includes(columnId)
      ? visibleColumns.filter(id => id !== columnId)
      : [...visibleColumns, columnId];
    updateVisibleColumns(newColumns);
  };

  // Reset columns to default
  const resetColumns = () => {
    updateVisibleColumns(DEFAULT_COLUMNS);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setDeskFilter('all');
    setActiveQuery(null);
    setAdvancedFilters({
      country: 'all',
      assignedTo: 'all',
      appType: 'all',
      agentId: 'all',
      dateFrom: '',
      dateTo: '',
      ageMin: '',
      ageMax: ''
    });
    setPage(1);
  };

  // Count active filters - include advanced filters
  const countAdvancedFilters = () => {
    let count = 0;
    if (advancedFilters.country && advancedFilters.country !== 'all') count++;
    if (advancedFilters.assignedTo && advancedFilters.assignedTo !== 'all') count++;
    if (advancedFilters.appType && advancedFilters.appType !== 'all') count++;
    if (advancedFilters.agentId && advancedFilters.agentId !== 'all') count++;
    if (advancedFilters.dateFrom) count++;
    if (advancedFilters.dateTo) count++;
    if (advancedFilters.ageMin) count++;
    if (advancedFilters.ageMax) count++;
    return count;
  };
  
  const activeFilterCount = (activeQuery?.groups?.reduce((sum, g) => sum + (g.conditions?.length || 0), 0) || 0) + countAdvancedFilters();

  // Is column visible helper
  const isColumnVisible = (columnId) => visibleColumns.includes(columnId);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending for dates, ascending for others
      setSortBy(column);
      setSortOrder(column === 'created_at' || column === 'app_age' || column === 'offer_age' ? 'desc' : 'asc');
    }
    setPage(1); // Reset to first page when sorting
  };

  // Render sortable header
  const SortableHeader = ({ column, children }) => {
    const isActive = sortBy === column;
    return (
      <button
        className="flex items-center gap-1 hover:text-foreground text-left"
        onClick={() => handleSort(column)}
        data-testid={`sort-${column}`}
      >
        {children}
        {isActive && (
          <span className="text-primary">
            {sortOrder === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </button>
    );
  };

  const fetchDesks = async () => {
    try {
      const response = await api().get('/desks');
      setDesks(response.data);
    } catch (error) {
      console.error('Failed to load desks');
    }
  };

  const fetchApplicationStatuses = async () => {
    try {
      const response = await api().get('/master-data?type=application_status');
      setApplicationStatuses(response.data || []);
    } catch (error) {
      console.error('Failed to load application statuses');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api().get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type - expanded support
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.pdf', '.doc', '.docx'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExt)) {
      toast.error('Please upload a JPEG, PNG, WEBP, GIF, PDF, DOC, or DOCX file');
      return;
    }

    // Check credits before extraction
    try {
      const creditRes = await api().get('/credits/balance');
      const balance = creditRes.data?.credits?.balance || 0;
      if (balance < 1) {
        toast.error(
          <div className="space-y-1">
            <p className="font-semibold">Out of Credits!</p>
            <p className="text-sm">You need at least 1 credit to scan documents. Each scan costs $0.20.</p>
            <button 
              onClick={() => window.location.href = '/billing'} 
              className="text-sm underline text-primary"
            >
              Top up credits →
            </button>
          </div>,
          { duration: 5000 }
        );
        return;
      }
    } catch (error) {
      console.error('Failed to check credits:', error);
    }

    setExtracting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api().post(
        `/applications/extract-document?document_type=${documentType}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        const extracted = response.data.extracted_data;
        
        // Trigger credit update in the header
        triggerCreditUpdate();
        
        // Map extracted data to form fields
        const updatedForm = { ...formData };
        
        if (documentType === 'passport') {
          if (extracted.first_name) updatedForm.first_name = extracted.first_name;
          if (extracted.last_name) updatedForm.last_name = extracted.last_name;
          if (extracted.full_name) {
            updatedForm.student_name = extracted.full_name;
            // If first/last not extracted, try to split full name
            if (!updatedForm.first_name && !updatedForm.last_name) {
              const parts = extracted.full_name.split(' ');
              updatedForm.first_name = parts[0] || '';
              updatedForm.last_name = parts.slice(1).join(' ') || '';
            }
          }
          if (extracted.date_of_birth) updatedForm.date_of_birth = extracted.date_of_birth;
          if (extracted.gender) updatedForm.gender = extracted.gender;
          if (extracted.nationality) updatedForm.nationality = extracted.nationality;
          if (extracted.passport_number) updatedForm.passport_number = extracted.passport_number;
          if (extracted.passport_issue_date) updatedForm.passport_issue_date = extracted.passport_issue_date;
          if (extracted.passport_expiry_date) updatedForm.passport_expiry_date = extracted.passport_expiry_date;
          
          // Generate student name if not set
          if (!updatedForm.student_name && (updatedForm.first_name || updatedForm.last_name)) {
            updatedForm.student_name = `${updatedForm.first_name} ${updatedForm.last_name}`.trim();
          }
        } else if (documentType === 'nic') {
          // NIC / National Identity Card
          if (extracted.first_name) updatedForm.first_name = extracted.first_name;
          if (extracted.last_name) updatedForm.last_name = extracted.last_name;
          if (extracted.full_name) {
            updatedForm.student_name = extracted.full_name;
            // If first/last not extracted, try to split full name
            if (!updatedForm.first_name && !updatedForm.last_name) {
              const parts = extracted.full_name.split(' ');
              updatedForm.first_name = parts[0] || '';
              updatedForm.last_name = parts.slice(1).join(' ') || '';
            }
          }
          if (extracted.date_of_birth) updatedForm.date_of_birth = extracted.date_of_birth;
          if (extracted.gender) updatedForm.gender = extracted.gender;
          if (extracted.nationality) updatedForm.nationality = extracted.nationality;
          // NIC specific fields
          if (extracted.nic_number) updatedForm.nic_number = extracted.nic_number;
          if (extracted.nic_issue_date) updatedForm.nic_issue_date = extracted.nic_issue_date;
          if (extracted.nic_expiry_date) updatedForm.nic_expiry_date = extracted.nic_expiry_date;
          if (extracted.address) updatedForm.address = extracted.address;
          if (extracted.city) updatedForm.city = extracted.city;
          if (extracted.province) updatedForm.province = extracted.province;
          
          // Generate student name if not set
          if (!updatedForm.student_name && (updatedForm.first_name || updatedForm.last_name)) {
            updatedForm.student_name = `${updatedForm.first_name} ${updatedForm.last_name}`.trim();
          }
        } else {
          // Resume
          if (extracted.first_name) updatedForm.first_name = extracted.first_name;
          if (extracted.last_name) updatedForm.last_name = extracted.last_name;
          if (extracted.full_name) {
            updatedForm.student_name = extracted.full_name;
          }
          if (extracted.email) updatedForm.student_email = extracted.email;
          if (extracted.phone) updatedForm.student_phone = extracted.phone;
          
          // Generate student name if not set
          if (!updatedForm.student_name && (updatedForm.first_name || updatedForm.last_name)) {
            updatedForm.student_name = `${updatedForm.first_name} ${updatedForm.last_name}`.trim();
          }
        }

        setFormData(updatedForm);
        toast.success(`Successfully extracted ${Object.keys(extracted).filter(k => extracted[k]).length} fields (1 credit deducted)`);
      } else {
        // Show specific error message from backend
        const errorMsg = response.data.error || 'Failed to extract data from document';
        const hint = response.data.hint;
        if (hint) {
          toast.error(`${errorMsg}\n${hint}`);
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Document extraction error:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error('Failed to extract document information. Please try again with a clearer image.');
      }
    } finally {
      setExtracting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAssign = async () => {
    if (!assignUserId || !assigningApp) return;
    try {
      await api().post(`/applications/${assigningApp.id}/assign`, { user_id: assignUserId });
      toast.success('Application assigned successfully');
      setAssignDialogOpen(false);
      setAssigningApp(null);
      setAssignUserId('');
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign application');
    }
  };

  const openAssignDialog = (app) => {
    setAssigningApp(app);
    setAssignUserId(app.assigned_to || '');
    setAssignDialogOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Build desk_data with counselor fields from extracted data
      const deskData = {
        counselor: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          dob: formData.date_of_birth,
          gender: formData.gender,
          nationality: formData.nationality,
          passport: formData.passport_number,
          passport_issue_date: formData.passport_issue_date,
          passport_expiry_date: formData.passport_expiry_date
        }
      };
      
      const submitData = {
        student_name: formData.student_name,
        student_email: formData.student_email,
        student_phone: formData.student_phone,
        desk_data: deskData
      };
      
      const response = await api().post('/applications', submitData);
      toast.success('Application created successfully');
      setCreateOpen(false);
      setFormData({ 
        student_name: '', student_email: '', student_phone: '',
        first_name: '', last_name: '', date_of_birth: '', gender: '',
        nationality: '', passport_number: '', passport_issue_date: '', passport_expiry_date: ''
      });
      navigate(`/applications/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    }
  };

  // Open delete confirmation dialog for single app
  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };
  
  // Execute single app deletion
  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await api().delete(`/applications/${deleteTargetId}`);
      toast.success('Application deleted successfully');
      fetchApplications();
      fetchDuplicates(); // Refresh duplicate detection after deletion
    } catch (error) {
      toast.error('Failed to delete application');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTargetId(null);
    }
  };

  const toggleSelectApp = (appId) => {
    setSelectedApps(prev => 
      prev.includes(appId) ? prev.filter(id => id !== appId) : [...prev, appId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedApps.length === applications.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(applications.map(app => app.id));
    }
  };

  const handleBulkShift = async () => {
    if (!bulkTargetDesk || selectedApps.length === 0) {
      toast.error('Please select applications and a target desk');
      return;
    }

    setBulkShifting(true);
    try {
      const response = await api().post('/applications/bulk-shift-desk', {
        application_ids: selectedApps,
        target_desk: bulkTargetDesk,
        notes: bulkNotes || null
      });

      if (response.data.success > 0) {
        toast.success(`Successfully shifted ${response.data.success} applications`);
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} applications could not be shifted`);
      }

      setBulkShiftOpen(false);
      setBulkTargetDesk('');
      setBulkNotes('');
      setSelectedApps([]);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk shift failed');
    } finally {
      setBulkShifting(false);
    }
  };

  // Open bulk delete confirmation dialog
  const handleBulkDeleteApps = () => {
    if (selectedApps.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };
  
  // Execute bulk deletion
  const confirmBulkDelete = async () => {
    setDeleting(true);
    try {
      // Delete all selected applications
      await Promise.all(selectedApps.map(id => api().delete(`/applications/${id}`)));
      toast.success(`${selectedApps.length} application(s) deleted successfully`);
      setSelectedApps([]);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete some applications');
      fetchApplications();
    } finally {
      setDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-500/10 text-gray-500',
      'in progress': 'bg-blue-500/10 text-blue-500',
      submitted: 'bg-yellow-500/10 text-yellow-500',
      accepted: 'bg-green-500/10 text-green-500',
      rejected: 'bg-red-500/10 text-red-500'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-500/10 text-gray-500';
  };

  const getDeskColor = (desk) => {
    const colors = {
      counselor: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      admission: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      status: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
      compliance: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      visa: 'bg-green-500/10 text-green-500 border-green-500/20',
      document: 'bg-orange-500/10 text-orange-500 border-orange-500/20'
    };
    return colors[desk?.toLowerCase()] || 'bg-primary/10 text-primary border-primary/20';
  };

  const clearFilters = () => {
    resetFilters();
  };

  const hasFilters = search || (statusFilter && statusFilter !== 'all') || (deskFilter && deskFilter !== 'all') || activeFilterCount > 0;

  // Fetch filter options on mount
  const [filterOptions, setFilterOptions] = useState({ 
    countries: [], 
    statuses: [], 
    desks: [], 
    users: [],
    universities: [],
    programs: []
  });
  const [countries, setCountries] = useState([]);
  const [degreeTypes, setDegreeTypes] = useState([]);
  
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await api().get('/applications/filters/options');
        setFilterOptions(response.data);
      } catch (error) {
        console.error('Failed to load filter options');
      }
    };
    fetchFilterOptions();
    
    // Fetch countries and degree types for document dialog
    const fetchMasterData = async () => {
      try {
        const [countriesRes, degreeTypesRes] = await Promise.all([
          api().get('/master-data?type=country'),
          api().get('/master-data?type=degree_type')
        ]);
        setCountries(countriesRes.data || []);
        setDegreeTypes(degreeTypesRes.data || []);
      } catch (error) {
        console.error('Failed to load master data');
      }
    };
    fetchMasterData();
    
    // Fetch application types and agents for filters
    const fetchAppTypesAndAgents = async () => {
      try {
        const [typesRes, agentsRes] = await Promise.all([
          api().get('/master-data?type=application_type'),
          api().get('/agents')
        ]);
        setApplicationTypes(typesRes.data || []);
        setAgents(agentsRes.data || []);
      } catch (error) {
        console.error('Failed to load app types and agents');
      }
    };
    fetchAppTypesAndAgents();
  }, []);

  // Build dynamic options for QueryBuilder
  const queryBuilderOptions = {
    countries: filterOptions.countries,
    statuses: filterOptions.statuses,
    desks: filterOptions.desks,
    users: filterOptions.users || users,
    universities: filterOptions.universities,
    programs: filterOptions.programs
  };

  // Pre-render elements using helper functions to avoid babel plugin issue
  const deskSelectItems = renderSelectItems(desks, 'slug', 'name');
  
  // Status select items from dynamic statuses
  const statusSelectItems = [];
  statusSelectItems.push(<SelectItem key="all" value="all">All Statuses</SelectItem>);
  for (let i = 0; i < (filterOptions.statuses || []).length; i++) {
    const s = filterOptions.statuses[i];
    statusSelectItems.push(
      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
    );
  }
  
  const columnMenuItems = renderColumnCheckboxItems(ALL_COLUMNS, visibleColumns, toggleColumn);
  
  // User assign select items
  const userAssignOptions = [];
  for (let i = 0; i < users.length; i++) {
    const u = users[i];
    userAssignOptions.push(
      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
    );
  }
  
  // Country select items for advanced filters
  const countrySelectItems = [];
  for (let i = 0; i < (filterOptions.countries || []).length; i++) {
    const c = filterOptions.countries[i];
    countrySelectItems.push(
      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
    );
  }
  
  // User select items for advanced filters
  const userSelectItems = [];
  for (let i = 0; i < (filterOptions.users || users || []).length; i++) {
    const u = (filterOptions.users || users)[i];
    userSelectItems.push(
      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
    );
  }

  // Application rows
  const applicationRows = [];
  for (let i = 0; i < applications.length; i++) {
    const app = applications[i];
    applicationRows.push(
      <ApplicationTableRow
        key={app.id}
        app={app}
        isColumnVisible={isColumnVisible}
        selectedApps={selectedApps}
        toggleSelectApp={toggleSelectApp}
        navigate={navigate}
        openAssignDialog={openAssignDialog}
        openDocShortfall={openDocShortfall}
        fetchDeskHistory={fetchDeskHistory}
        openChat={openChat}
        openAgentChat={openAgentChat}
        openReminder={openReminder}
        openActivityLog={openActivityLog}
        openEmailCompose={openEmailCompose}
        openSMSCompose={openSMSCompose}
        openWhatsAppCompose={openWhatsAppCompose}
        openContentWriter={openContentWriter}
        openVettingDialog={openVettingDialog}
        openDuplicateDialog={openDuplicateDialog}
        handleDelete={handleDelete}
        unreadCount={unreadCounts[app.id] || 0}
        reminderStats={reminderStats[app.id] || { sent: 0, pending: 0 }}
        getDeskColor={getDeskColor}
        getStatusColor={getStatusColor}
        isUnread={isAppUnread(app.id)}
        onMarkAsRead={markAppAsRead}
        isAdmin={tabCounts?.is_admin || false}
        currentTab={activeTab}
        currentPage={page}
        currentLimit={recordsPerPage}
        duplicateInfo={duplicates[app.id] || null}
      />
    );
  }

  return (
    <>
      {/* CSS for unread indicator */}
      <style>{`
        .unread-row {
          border-left: 3px solid #84cc16;
          background: linear-gradient(to right, rgba(132, 204, 22, 0.08), transparent 20%);
        }
        .unread-row:hover {
          background: linear-gradient(to right, rgba(132, 204, 22, 0.12), rgba(0,0,0,0.02) 20%);
        }
        .unread-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #84cc16;
          flex-shrink: 0;
        }
        .high-reminders-row {
          border-left: 3px solid #ef4444;
          background: linear-gradient(to right, rgba(239, 68, 68, 0.08), transparent 20%);
        }
        .high-reminders-row:hover {
          background: linear-gradient(to right, rgba(239, 68, 68, 0.12), rgba(0,0,0,0.02) 20%);
        }
        .reminder-warning-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #ef4444;
          flex-shrink: 0;
        }
        .duplicate-row {
          border-left: 3px solid #f97316;
          background: linear-gradient(to right, rgba(249, 115, 22, 0.1), transparent 20%);
        }
        .duplicate-row:hover {
          background: linear-gradient(to right, rgba(249, 115, 22, 0.15), rgba(0,0,0,0.02) 20%);
        }
        .duplicate-badge {
          background-color: #f97316;
          color: white;
          font-size: 0.65rem;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 6px;
          font-weight: 600;
        }
      `}</style>
      
      {/* Single Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this application? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedApps.length} Application(s)</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedApps.length} selected application(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmBulkDelete} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    <div className="space-y-6" data-testid="applications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {deskView === 'my' ? 'My Applications' : 
             deskView === 'admission_coordinator' ? 'Admission Coordinator' :
             deskView === 'admission_officer' ? 'Admission Officer' :
             'Applications'}
          </h1>
          <p className="text-muted-foreground">
            {deskView === 'my' ? 'Applications you own or created' : 
             deskView === 'admission_coordinator' ? 'Applications on Admission Coordinator desk' :
             deskView === 'admission_officer' ? 'Applications on Admission Officer desk' :
             'Manage student applications through the workflow'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* r66.9 — Admin escape hatch: backfill app_numbers for any apps missing one */}
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const r = await api().post('/applications/backfill-app-numbers');
                const { scanned, assigned, top_app_number } = r.data;
                if (assigned > 0) {
                  toast.success(`Assigned app numbers to ${assigned} application(s) · highest is now #${top_app_number}.`);
                  fetchApplications();
                } else {
                  toast.success('All applications already have an app number ✓');
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Backfill failed');
              }
            }}
            data-testid="backfill-app-numbers-btn"
            title="Assign numbers to any applications missing one"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Fix App Numbers
          </Button>
          <CSVExportButton entityType="applications" entityLabel="Applications" />
          <CSVImportModal entityType="applications" entityLabel="Applications" onSuccess={fetchApplications} />
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-application-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Application</DialogTitle>
                <DialogDescription>Start a new student application or upload a document to auto-fill</DialogDescription>
              </DialogHeader>
              
              {/* Document Upload Section */}
              <div className="border-2 border-dashed border-muted rounded-lg p-4 bg-muted/30">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <Label className="text-sm font-medium">AI Auto-Fill from Document</Label>
                      <CreditCostBadge feature={CREDIT_FEATURES.PROFILE_EXTRACTION} />
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Upload a passport, NIC (National ID), or resume to automatically extract applicant information
                    </p>
                    <div className="flex items-center gap-2">
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger className="w-[140px]" data-testid="doc-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="passport">Passport</SelectItem>
                          <SelectItem value="nic">NIC/National ID</SelectItem>
                          <SelectItem value="resume">Resume/CV</SelectItem>
                        </SelectContent>
                      </Select>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleDocumentUpload}
                        accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,image/jpeg,image/png,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        data-testid="doc-upload-input"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={extracting}
                        data-testid="upload-doc-btn"
                      >
                        {extracting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload {documentType === 'passport' ? 'Passport' : documentType === 'nic' ? 'NIC' : 'Resume'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleCreate} className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Extracted from document"
                      data-testid="app-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Extracted from document"
                      data-testid="app-lastname-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Full Name <span className="text-destructive">*</span></Label>
                  <Input
                    value={formData.student_name}
                    onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                    required
                    data-testid="app-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email <span className="text-destructive">*</span></Label>
                    <Input
                      type="email"
                      value={formData.student_email}
                      onChange={(e) => setFormData({ ...formData, student_email: e.target.value })}
                      required
                      data-testid="app-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <PhoneInput
                      value={formData.student_phone}
                      onChange={(value) => setFormData({ ...formData, student_phone: value })}
                      data-testid="app-phone-input"
                    />
                  </div>
                </div>

                {/* Passport Info (shown if extracted) */}
                {(formData.passport_number || formData.date_of_birth || formData.nationality) && (
                  <div className="border rounded-lg p-4 bg-muted/20">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-primary" />
                      <Label className="text-sm font-medium">Extracted Passport Details</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Passport Number</Label>
                        <Input
                          value={formData.passport_number}
                          onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                          data-testid="app-passport-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Nationality</Label>
                        <Input
                          value={formData.nationality}
                          onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                          data-testid="app-nationality-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Date of Birth</Label>
                        <Input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                          data-testid="app-dob-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Passport Expiry</Label>
                        <Input
                          type="date"
                          value={formData.passport_expiry_date}
                          onChange={(e) => setFormData({ ...formData, passport_expiry_date: e.target.value })}
                          data-testid="app-expiry-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <Button type="submit" className="w-full" data-testid="app-submit-btn">
                  Create Application
                </Button>
              </form>
            </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Top Row: Search + Quick Filters + Controls */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by student name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="app-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="app-filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {applicationStatuses.length > 0 ? (
                    applicationStatuses.map(s => (
                      <SelectItem key={s.id} value={s.name.toLowerCase().replace(/\s+/g, '_')}>{s.name}</SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              <Select value={deskFilter} onValueChange={setDeskFilter}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="app-filter-desk">
                  <SelectValue placeholder="All Desks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Desks</SelectItem>
                  {deskSelectItems}
                </SelectContent>
              </Select>
              
              {/* Advanced Filters Button */}
              <Popover open={showAdvancedFilters} onOpenChange={setShowAdvancedFilters}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="relative" data-testid="advanced-filters-btn">
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    Filters
                    {activeFilterCount > 0 && (
                      <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center rounded-full bg-primary text-[10px]">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Advanced Filters</h4>
                      <Button variant="ghost" size="sm" onClick={resetFilters}>
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Reset
                      </Button>
                    </div>
                    
                    {/* Country Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">Country</Label>
                      <Select 
                        value={advancedFilters.country} 
                        onValueChange={(val) => setAdvancedFilters(prev => ({...prev, country: val}))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="All Countries" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Countries</SelectItem>
                          {countrySelectItems}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Assigned To Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">Assigned To</Label>
                      <Select 
                        value={advancedFilters.assignedTo} 
                        onValueChange={(val) => setAdvancedFilters(prev => ({...prev, assignedTo: val}))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="All Users" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Users</SelectItem>
                          {userSelectItems}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Application Type Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">Application Type</Label>
                      <Select 
                        value={advancedFilters.appType} 
                        onValueChange={(val) => setAdvancedFilters(prev => ({...prev, appType: val}))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {applicationTypes.length > 0 ? (
                            applicationTypes.map(type => (
                              <SelectItem key={type.id} value={type.name.toLowerCase().replace(/\s+/g, '_')}>
                                {type.name}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="direct">Direct</SelectItem>
                              <SelectItem value="agent">Agent</SelectItem>
                              <SelectItem value="office">Office</SelectItem>
                              <SelectItem value="web_form">Web Form</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Agent Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">Agent</Label>
                      <Select 
                        value={advancedFilters.agentId} 
                        onValueChange={(val) => setAdvancedFilters(prev => ({...prev, agentId: val}))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="All Agents" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Agents</SelectItem>
                          {agents.map(agent => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name} ({agent.username})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Date Range Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">Created Date Range</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="date"
                          value={advancedFilters.dateFrom}
                          onChange={(e) => setAdvancedFilters(prev => ({...prev, dateFrom: e.target.value}))}
                          className="h-8 text-xs"
                          placeholder="From"
                        />
                        <Input
                          type="date"
                          value={advancedFilters.dateTo}
                          onChange={(e) => setAdvancedFilters(prev => ({...prev, dateTo: e.target.value}))}
                          className="h-8 text-xs"
                          placeholder="To"
                        />
                      </div>
                    </div>
                    
                    {/* App Age Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">App Age (Days)</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          value={advancedFilters.ageMin}
                          onChange={(e) => setAdvancedFilters(prev => ({...prev, ageMin: e.target.value}))}
                          className="h-8"
                          placeholder="Min"
                          min="0"
                        />
                        <Input
                          type="number"
                          value={advancedFilters.ageMax}
                          onChange={(e) => setAdvancedFilters(prev => ({...prev, ageMax: e.target.value}))}
                          className="h-8"
                          placeholder="Max"
                          min="0"
                        />
                      </div>
                    </div>
                    
                    <Button className="w-full" size="sm" onClick={() => setShowAdvancedFilters(false)}>
                      Apply Filters
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Column Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="column-selector-btn">
                    <Columns className="w-4 h-4 mr-2" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm font-medium">Toggle Columns</div>
                  <DropdownMenuSeparator />
                  {columnMenuItems}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={resetColumns}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset to Default
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {hasFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters} title="Clear all filters">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedApps.length > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{selectedApps.length} application(s) selected</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedApps([])}>
                  Clear Selection
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={handleBulkDeleteApps}
                  data-testid="bulk-delete-apps-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
                <Dialog open={bulkShiftOpen} onOpenChange={setBulkShiftOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="bulk-shift-btn">
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Bulk Shift Desk
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Bulk Shift Applications</DialogTitle>
                      <DialogDescription>
                        Move {selectedApps.length} selected application(s) to a different desk
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Target Desk</Label>
                        <Select value={bulkTargetDesk} onValueChange={setBulkTargetDesk}>
                          <SelectTrigger data-testid="bulk-target-desk">
                            <SelectValue placeholder="Select target desk" />
                          </SelectTrigger>
                          <SelectContent>
                            {deskSelectItems}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea
                          value={bulkNotes}
                          onChange={(e) => setBulkNotes(e.target.value)}
                          placeholder="Add notes for this bulk action..."
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setBulkShiftOpen(false)}>Cancel</Button>
                      <Button onClick={handleBulkShift} disabled={!bulkTargetDesk || bulkShifting} data-testid="confirm-bulk-shift">
                        {bulkShifting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
                        Shift {selectedApps.length} Applications
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Application Tabs - New UI/UX Design */}
      <div className="flex items-center justify-between border-b border-border bg-card rounded-t-lg px-2 pt-2">
        <div className="flex items-center gap-1">
          {/* Admin & Super Admin View: Single "All Applications" Tab with Rich Filters */}
          {(isSuperAdmin || isAdmin) ? (
            <button
              onClick={() => { setActiveTab('all_applications'); setPage(1); setSelectedApps([]); }}
              className="group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md bg-amber-500 text-white shadow-md"
              data-testid="tab-all-applications"
            >
              <Inbox className="w-4 h-4 text-white" />
              <span>All Applications</span>
              <Badge className="text-xs bg-white/20 text-white">
                {tabCounts.all_applications || 0}
              </Badge>
            </button>
          ) : (
            <>
              {/* Team Admin only: "Applications by Team" default tab —
                  shows every application processed by any member of the
                  admin's team, powered by the Team Admin scope on the
                  applications endpoint. */}
              {isTeamAdmin && (
                <button
                  onClick={() => { setActiveTab('team_applications'); setPage(1); setSelectedApps([]); }}
                  className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md ${
                    activeTab === 'team_applications'
                      ? 'bg-[#1A2A4F] text-white shadow-md'
                      : 'text-muted-foreground hover:text-foreground hover:bg-slate-200 dark:hover:bg-slate-800'
                  }`}
                  data-testid="tab-team-applications"
                >
                  <Users className={`w-4 h-4 ${activeTab === 'team_applications' ? 'text-white' : 'text-[#1A2A4F] dark:text-slate-200'}`} />
                  <span>Applications by Team</span>
                  <Badge className={`text-xs ${activeTab === 'team_applications' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100'}`}>
                    {tabCounts.team_applications || 0}
                  </Badge>
                </button>
              )}

              {/* Tab 1: Leads (No Doc) - Red/Orange theme */}
              <button
                onClick={() => { setActiveTab('leads_no_doc'); setPage(1); setSelectedApps([]); }}
                className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md ${
                  activeTab === 'leads_no_doc' 
                    ? 'bg-red-500 text-white shadow-md' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-red-100 dark:hover:bg-red-900/30'
                }`}
                data-testid="tab-leads-no-doc"
              >
                <FileX className={`w-4 h-4 ${activeTab === 'leads_no_doc' ? 'text-white' : 'text-red-500'}`} />
                <span>Leads (No Doc)</span>
                <Badge className={`text-xs ${activeTab === 'leads_no_doc' ? 'bg-white/20 text-white' : 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                  {tabCounts.leads_no_doc || 0}
                </Badge>
              </button>

          {/* Tab 2: Leads (Incomplete Doc) - Yellow/Amber theme */}
          <button
            onClick={() => { setActiveTab('leads_incomplete_doc'); setPage(1); setSelectedApps([]); }}
            className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md ${
              activeTab === 'leads_incomplete_doc' 
                ? 'bg-amber-500 text-white shadow-md' 
                : 'text-muted-foreground hover:text-foreground hover:bg-amber-100 dark:hover:bg-amber-900/30'
            }`}
            data-testid="tab-leads-incomplete-doc"
          >
            <FileQuestion className={`w-4 h-4 ${activeTab === 'leads_incomplete_doc' ? 'text-white' : 'text-amber-500'}`} />
            <span>Apps (Incomplete)</span>
            <Badge className={`text-xs ${activeTab === 'leads_incomplete_doc' ? 'bg-white/20 text-white' : 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200'}`}>
              {tabCounts.leads_incomplete_doc || 0}
            </Badge>
          </button>

          {/* Tab 3: Forwarded Cases - Blue theme */}
          <button
            onClick={() => { setActiveTab('forwarded_cases'); setPage(1); setSelectedApps([]); }}
            className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md ${
              activeTab === 'forwarded_cases' 
                ? 'bg-blue-500 text-white shadow-md' 
                : 'text-muted-foreground hover:text-foreground hover:bg-blue-100 dark:hover:bg-blue-900/30'
            }`}
            data-testid="tab-forwarded-cases"
          >
            <Forward className={`w-4 h-4 ${activeTab === 'forwarded_cases' ? 'text-white' : 'text-blue-500'}`} />
            <span>Forwarded Apps</span>
            <Badge className={`text-xs ${activeTab === 'forwarded_cases' ? 'bg-white/20 text-white' : 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200'}`}>
              {tabCounts.forwarded_cases || 0}
            </Badge>
          </button>

          {/* Tab 4: Pending App Reviews - Purple/Indigo theme - Available to ALL roles */}
          <button
            onClick={() => { setActiveTab('pending_review'); setPage(1); setSelectedApps([]); }}
            className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md ${
              activeTab === 'pending_review' 
                ? 'bg-amber-500 text-white shadow-md' 
                : 'text-muted-foreground hover:text-foreground hover:bg-amber-100 dark:hover:bg-slate-800/30'
            }`}
            data-testid="tab-pending-review"
          >
            <Inbox className={`w-4 h-4 ${activeTab === 'pending_review' ? 'text-white' : 'text-amber-500'}`} />
            <span>Pending App Reviews</span>
              <Badge className={`text-xs ${activeTab === 'pending_review' ? 'bg-white/20 text-white' : 'bg-amber-200 text-slate-800 dark:bg-slate-800 dark:text-amber-200'}`}>
                {tabCounts.pending_review || 0}
              </Badge>
              {/* Unread notification dot */}
              {tabCounts.pending_review > 0 && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                </span>
              )}
            </button>

          {/* Tab: Unassigned Leads - Admin Only - Green theme */}
          {isAdmin && (
            <button
              onClick={() => { setActiveTab('unassigned_leads'); setPage(1); setSelectedApps([]); }}
              className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md ${
                activeTab === 'unassigned_leads' 
                  ? 'bg-emerald-600 text-white shadow-md' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
              }`}
              data-testid="tab-unassigned-leads"
            >
              <User className={`w-4 h-4 ${activeTab === 'unassigned_leads' ? 'text-white' : 'text-emerald-500'}`} />
              <span>Unassigned</span>
              <Badge className={`text-xs ${activeTab === 'unassigned_leads' ? 'bg-white/20 text-white' : 'bg-emerald-200 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'}`}>
                {tabCounts.unassigned_leads || 0}
              </Badge>
            </button>
          )}

          
          {/* Custom Tabs - Filter out null/empty tab names */}
          {Object.entries(customTabs)
            .filter(([tabName]) => tabName && tabName !== 'null' && tabName.trim())
            .map(([tabName, count]) => {
            const tabColor = customTabColors[tabName] || '#f97316';
            return (
              <button
                key={tabName}
                onClick={() => { setActiveTab(tabName); setPage(1); setSelectedApps([]); }}
                className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all rounded-t-md ${
                  activeTab === tabName 
                    ? 'text-white shadow-md' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                style={{
                  backgroundColor: activeTab === tabName ? tabColor : 'transparent',
                  ':hover': { backgroundColor: activeTab !== tabName ? `${tabColor}20` : tabColor }
                }}
                onMouseEnter={(e) => { if (activeTab !== tabName) e.target.style.backgroundColor = `${tabColor}20`; }}
                onMouseLeave={(e) => { if (activeTab !== tabName) e.target.style.backgroundColor = 'transparent'; }}
                data-testid={`tab-custom-${tabName.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span>{tabName}</span>
                <Badge 
                  className="text-xs"
                  style={{
                    backgroundColor: activeTab === tabName ? 'rgba(255,255,255,0.2)' : `${tabColor}30`,
                    color: activeTab === tabName ? 'white' : tabColor
                  }}
                >
                  {count}
                </Badge>
              </button>
            );
          })}
            </>
          )}
          
          {/* + Create Tab Button - Only for non-Super Admin */}
          {!isSuperAdmin && (
          <Dialog open={createTabOpen} onOpenChange={setCreateTabOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-t-md transition-all border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 ml-1"
                data-testid="create-tab-btn"
              >
                <Plus className="w-4 h-4" />
                <span>Create Tab</span>
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Tab</DialogTitle>
                <DialogDescription>
                  Create a custom tab to organize your applications
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="tab-name">Tab Name</Label>
                  <Input
                    id="tab-name"
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                    placeholder="e.g., Rejected, On Hold, Pending Review"
                    data-testid="new-tab-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tab Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {tabColors.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setNewTabColor(color.value)}
                        className={`w-8 h-8 rounded-full transition-all ${
                          newTabColor === color.value 
                            ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' 
                            : 'hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                        data-testid={`color-${color.name.toLowerCase()}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selected: <span style={{ color: newTabColor }}>{tabColors.find(c => c.value === newTabColor)?.name || 'Custom'}</span>
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateTabOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateTab} 
                  disabled={!newTabName.trim() || creatingTab}
                  style={{ backgroundColor: newTabColor }}
                  className="text-white hover:opacity-90"
                  data-testid="confirm-create-tab"
                >
                  {creatingTab ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Tab
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Tab Shift Button - Only show when applications are selected and not Super Admin */}
        {selectedApps.length > 0 && !isSuperAdmin && (
          <Dialog open={tabShiftOpen} onOpenChange={setTabShiftOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="mr-2" data-testid="tab-shift-btn">
                <FolderInput className="w-4 h-4 mr-2" />
                Move to Tab
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Move Applications to Another Tab</DialogTitle>
                <DialogDescription>
                  Move {selectedApps.length} selected application(s) to a different tab
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Target Tab</Label>
                  <Select value={tabShiftTarget} onValueChange={setTabShiftTarget}>
                    <SelectTrigger data-testid="tab-shift-target">
                      <SelectValue placeholder="Select target tab" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTab !== 'fresh_leads' && (
                        <SelectItem value="fresh_leads">{tabNames.tab1_name}</SelectItem>
                      )}
                      {activeTab !== 'active_applications' && (
                        <SelectItem value="active_applications">{tabNames.tab2_name}</SelectItem>
                      )}
                      {activeTab !== 'my_cases' && (
                        <SelectItem value="my_cases">{tabNames.tab3_name}</SelectItem>
                      )}
                      {activeTab !== 'pending_review' && (
                        <SelectItem value="pending_review">Pending Review</SelectItem>
                      )}
                      {isAdmin && activeTab !== 'unassigned_leads' && (
                        <SelectItem value="unassigned_leads">{tabNames.tab4_name}</SelectItem>
                      )}
                      {/* Custom tabs */}
                      {Object.keys(customTabs).map(tabName => (
                        activeTab !== tabName && (
                          <SelectItem key={tabName} value={tabName}>{tabName}</SelectItem>
                        )
                      ))}
                      {/* Option to create new custom tab */}
                      <SelectItem value="__new_custom_tab__" className="text-blue-600 font-medium">
                        + Create New Tab...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* New custom tab name input */}
                {tabShiftTarget === '__new_custom_tab__' && (
                  <div className="space-y-2">
                    <Label>New Tab Name</Label>
                    <Input
                      value={newCustomTabName}
                      onChange={(e) => setNewCustomTabName(e.target.value)}
                      placeholder="e.g., Rejected, On Hold, Pending Review"
                      data-testid="new-custom-tab-name"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={tabShiftNotes}
                    onChange={(e) => setTabShiftNotes(e.target.value)}
                    placeholder="Add notes for this tab shift..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTabShiftOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleTabShift} 
                  disabled={!tabShiftTarget || tabShifting || (tabShiftTarget === '__new_custom_tab__' && !newCustomTabName.trim())} 
                  data-testid="confirm-tab-shift"
                >
                  {tabShifting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderInput className="w-4 h-4 mr-2" />}
                  Move {selectedApps.length} Applications
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Distribute Leads Button - Show on Fresh Leads tab */}
        {activeTab === 'fresh_leads' && tabCounts.fresh_leads > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDistributeLeads}
            disabled={distributing}
            className="ml-auto"
            data-testid="distribute-leads-btn"
          >
            {distributing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <UserCircle className="w-4 h-4 mr-2" />
            )}
            Distribute Leads
          </Button>
        )}

        {/* Global Desk Shifting Button - Available on all tabs when apps are selected */}
        {selectedApps.length > 0 && (
          <Dialog open={globalDeskShiftOpen} onOpenChange={setGlobalDeskShiftOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="default" 
                size="sm" 
                className="ml-2 bg-blue-600 hover:bg-blue-700"
                data-testid="global-desk-shift-btn"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Desk Shifting ({selectedApps.length})
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Shift Applications to Another Desk</DialogTitle>
                <DialogDescription>
                  Move {selectedApps.length} selected application(s) to a different processing desk
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Target Desk</Label>
                  <Select value={bulkTargetDesk} onValueChange={setBulkTargetDesk}>
                    <SelectTrigger data-testid="global-desk-shift-target">
                      <SelectValue placeholder="Select target desk" />
                    </SelectTrigger>
                    <SelectContent>
                      {deskSelectItems}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={bulkNotes}
                    onChange={(e) => setBulkNotes(e.target.value)}
                    placeholder="Add notes for this desk shift..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setGlobalDeskShiftOpen(false)}>Cancel</Button>
                <Button 
                  onClick={async () => {
                    await handleBulkShift();
                    setGlobalDeskShiftOpen(false);
                  }} 
                  disabled={!bulkTargetDesk || bulkShifting} 
                  data-testid="confirm-global-desk-shift"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {bulkShifting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
                  Shift {selectedApps.length} Applications
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Table */}
      <Card className="rounded-t-none border-t-0">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : applications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <p>No applications found</p>
              <Button variant="link" onClick={() => setCreateOpen(true)}>
                Create your first application
              </Button>
            </div>
          ) : (
            <div className="relative app-table-wrap overflow-auto scroll-smooth max-h-[calc(100vh-260px)]"
                 data-testid="applications-table-scroll-wrap">
            <table className="w-full caption-bottom text-sm min-w-[1900px]">
              <TableHeader className="[&_tr]:border-b">
                <TableRow>
                  {isColumnVisible('checkbox') && (
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedApps.length === applications.length && applications.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-apps"
                      />
                    </TableHead>
                  )}
                  {isColumnVisible('app_number') && (
                    <TableHead>
                      <SortableHeader column="app_number">App #</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('student_name') && (
                    <TableHead>
                      <SortableHeader column="student_name">Student</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('student_email') && <TableHead>Email</TableHead>}
                  {isColumnVisible('student_phone') && <TableHead>Phone</TableHead>}
                  {isColumnVisible('country') && (
                    <TableHead>
                      <SortableHeader column="country">Country</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('partner_university') && (
                    <TableHead>
                      <SortableHeader column="partner_university">Partner University</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('app_type') && <TableHead>Type</TableHead>}
                  {isColumnVisible('assigned_to') && (
                    <TableHead>
                      <SortableHeader column="assigned_to">Case Owner</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('current_desk') && (
                    <TableHead>
                      <SortableHeader column="current_desk">Current Desk</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('status') && (
                    <TableHead>
                      <SortableHeader column="status">Status</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('documents') && <TableHead>Docs</TableHead>}
                  {isColumnVisible('reminders') && <TableHead>Reminders</TableHead>}
                  {isColumnVisible('created_at') && (
                    <TableHead>
                      <SortableHeader column="created_at">Created</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('app_age') && (
                    <TableHead>
                      <SortableHeader column="app_age">App Age</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('offer_age') && (
                    <TableHead>
                      <SortableHeader column="offer_age">Offer Age</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('apply_date') && (
                    <TableHead>
                      <SortableHeader column="desk_data.admission.apply_date">Apply Date to Uni</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('offer_date') && (
                    <TableHead>
                      <SortableHeader column="desk_data.admission.date_of_offer">Date of Offer</SortableHeader>
                    </TableHead>
                  )}
                  {isColumnVisible('actions') && <TableHead className="app-tbl-stick-right1 w-[100px] bg-background sticky right-[50px] shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.18)] z-20">Actions</TableHead>}
                  {isColumnVisible('more') && <TableHead className="app-tbl-stick-right0 w-[50px] bg-background sticky right-0 z-20"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {applicationRows}
              </TableBody>
            </table>
            </div>
          )}
          {/* Floating horizontal slider — always visible while the table overflows */}
          <FloatingHScrollSlider targetSelector='[data-testid="applications-table-scroll-wrap"]' label="Drag to slide table" />
        </CardContent>

        {/* Footer with Pagination and Records Info */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <div className="flex items-center gap-4">
            <p className="text-sm text-muted-foreground">
              Showing <span className="font-medium">{applications.length}</span> of <span className="font-medium">{totalRecords}</span> applications
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page:</span>
              <Select value={String(recordsPerPage)} onValueChange={(val) => { setRecordsPerPage(Number(val)); setPage(1); }}>
                <SelectTrigger className="w-[70px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Assign Dialog - Admin Only */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="w-5 h-5" />
              Change Case Owner
            </DialogTitle>
            <DialogDescription>
              Assign this application to a different Case Owner (Admin only)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {assigningApp && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium">{assigningApp.student_name}</p>
                <p className="text-sm text-muted-foreground">{assigningApp.student_email}</p>
                <p className="text-xs text-muted-foreground mt-1">Current Case Owner: {assigningApp.assigned_to_name || 'Unassigned'}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>New Case Owner</Label>
              <Select value={assignUserId || "unassigned"} onValueChange={(val) => setAssignUserId(val === "unassigned" ? "" : val)}>
                <SelectTrigger data-testid="assign-user-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {userAssignOptions}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign} data-testid="confirm-assign-btn">
              <UserCircle className="w-4 h-4 mr-2" />
              Change Case Owner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Chat */}
      <ApplicationChat
        open={chatOpen}
        onOpenChange={(open) => {
          setChatOpen(open);
          if (!open) {
            fetchUnreadCounts();
          }
        }}
        application={chatApplication}
      />

      {/* Agent Chat Dialog */}
      <Dialog open={agentChatOpen} onOpenChange={setAgentChatOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <span>Chat with Agent</span>
                <p className="text-xs font-normal text-muted-foreground">
                  {agentChatApplication?.transferred_by_agent_name || 'Agent'}
                </p>
              </div>
            </DialogTitle>
            <DialogDescription>
              Re: {agentChatApplication?.student_name}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 max-h-[350px] border rounded-lg p-4 bg-muted/30">
            {agentChatMessages.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p>No messages yet</p>
                <p className="text-sm">Start the conversation with the agent</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agentChatMessages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex ${msg.sender_type === 'org_team' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      msg.sender_type === 'org_team' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-blue-100 text-blue-900'
                    }`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_type === 'org_team' ? 'opacity-70' : 'text-blue-600'
                      }`}>
                        {msg.sender_name} • {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="Type your message..."
              value={agentChatNewMessage}
              onChange={(e) => setAgentChatNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendAgentChatMessage()}
              disabled={agentChatSending}
            />
            <Button onClick={sendAgentChatMessage} disabled={agentChatSending || !agentChatNewMessage.trim()}>
              {agentChatSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <ReminderDialog
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        application={reminderApplication}
      />

      {/* Document Shortfall Dialog */}
      <Dialog open={docShortfallOpen} onOpenChange={setDocShortfallOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document Status
            </DialogTitle>
            <DialogDescription>
              {docShortfallApp?.student_name} - {docShortfallApp?.university_name || 'No university selected'}
            </DialogDescription>
          </DialogHeader>
          
          {loadingDocs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Show missing fields prompt if nationality or degree type is missing */}
              {(missingFields.nationality || missingFields.degreeType) ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900">
                        <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-amber-800 dark:text-amber-200">Missing Information</h4>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          To determine the exact required documents list, please provide the following information:
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {missingFields.nationality && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Nationality / Country <span className="text-destructive">*</span>
                        </Label>
                        <Select value={docDialogNationality} onValueChange={setDocDialogNationality}>
                          <SelectTrigger data-testid="doc-dialog-nationality">
                            <SelectValue placeholder="Select nationality/country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map(country => (
                              <SelectItem key={country.id} value={country.id}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          This determines the country-specific document requirements
                        </p>
                      </div>
                    )}
                    
                    {missingFields.degreeType && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Degree Type <span className="text-destructive">*</span>
                        </Label>
                        <Select value={docDialogDegreeType} onValueChange={setDocDialogDegreeType}>
                          <SelectTrigger data-testid="doc-dialog-degree-type">
                            <SelectValue placeholder="Select degree type" />
                          </SelectTrigger>
                          <SelectContent>
                            {degreeTypes.map(dt => (
                              <SelectItem key={dt.id} value={dt.id}>
                                {dt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Different degree levels have different document requirements
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={saveDocDialogFields}
                    disabled={savingDocFields || (missingFields.nationality && !docDialogNationality) || (missingFields.degreeType && !docDialogDegreeType)}
                    className="w-full"
                    data-testid="save-doc-fields-btn"
                  >
                    {savingDocFields ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save & Show Required Documents
                      </>
                    )}
                  </Button>
                </div>
              ) : requiredDocs.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No required documents configured for this university.</p>
                  <p className="text-sm">Configure required documents in Core Settings → Required Docs</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {getMissingDocs().length > 0 ? (
                      <>
                        <p className="text-sm font-medium text-amber-600">Missing Documents ({getMissingDocs().length})</p>
                        <div className="space-y-2 max-h-[250px] overflow-y-auto">
                          {getMissingDocs().map((doc, index) => (
                            <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-800 dark:text-amber-200">{doc}</span>
                              </div>
                              <Badge variant="outline" className="border-amber-500 text-amber-600">
                                Not Submitted
                              </Badge>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-4 border-t">
                          <Button 
                            onClick={sendDocShortfallWhatsApp}
                            className="w-full bg-green-600 hover:bg-green-700"
                            disabled={!docShortfallApp?.student_phone}
                          >
                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            Send Reminder on WhatsApp
                          </Button>
                          {!docShortfallApp?.student_phone && (
                            <p className="text-xs text-muted-foreground text-center mt-2">
                              No phone number available for this student
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                          <FileText className="w-6 h-6 text-green-600" />
                        </div>
                        <p className="font-medium text-green-600">All Documents Submitted!</p>
                        <p className="text-sm text-muted-foreground mt-1">{uploadedDocs.length}/{requiredDocs.length} required documents received</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Compose Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Email
            </DialogTitle>
            <DialogDescription>
              Send an email to {emailApp?.student_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-3 bg-muted rounded-lg text-sm">
              <div>
                <span className="text-muted-foreground">To:</span>
                <span className="ml-2 font-medium">{emailApp?.student_email}</span>
              </div>
              <div>
                <span className="text-muted-foreground">From:</span>
                <span className="ml-2 font-medium">Organization Email</span>
              </div>
            </div>
            
            {emailTemplates.length > 0 && (
              <div className="space-y-2">
                <Label>Use Template</Label>
                <Select onValueChange={(value) => {
                  const template = emailTemplates.find(t => t.id === value);
                  if (template) applyEmailTemplate(template);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email_subject">Subject *</Label>
              <Input
                id="email_subject"
                placeholder="Enter email subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                data-testid="email-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email_body">Message *</Label>
              <Textarea
                id="email_body"
                placeholder="Enter your message..."
                rows={8}
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                data-testid="email-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} data-testid="send-email-btn">
              {sendingEmail && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS Compose Dialog */}
      <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Send SMS
            </DialogTitle>
            <DialogDescription>
              Send an SMS to {smsApp?.student_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm">
              <span className="text-muted-foreground">To:</span>
              <span className="ml-2 font-medium font-mono">{smsApp?.student_phone}</span>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sms_message">Message *</Label>
              <Textarea
                id="sms_message"
                placeholder="Type your SMS message..."
                rows={4}
                maxLength={1600}
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                data-testid="sms-message-input"
              />
              <p className="text-xs text-muted-foreground text-right">
                {smsMessage.length}/1600 characters
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>Cancel</Button>
            <Button onClick={sendSMS} disabled={sendingSMS} data-testid="send-sms-btn">
              {sendingSMS && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <MessageSquare className="w-4 h-4 mr-2" />
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Compose Dialog (routes through connected Chrome extension) */}
      <Dialog open={waDialogOpen} onOpenChange={setWaDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#25D366" xmlns="http://www.w3.org/2000/svg">
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.821 11.821 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.298-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
              </svg>
              Send WhatsApp Message
            </DialogTitle>
            <DialogDescription>
              Queued for delivery via your connected WhatsApp Chrome extension
              {waApp?.student_name ? ` to ${waApp.student_name}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wa_phone">Phone (with country code)</Label>
              <Input
                id="wa_phone"
                placeholder="+923001234567"
                value={waPhone}
                onChange={(e) => setWaPhone(e.target.value)}
                data-testid="wa-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wa_message">Message *</Label>
              <Textarea
                id="wa_message"
                placeholder="Type your WhatsApp message..."
                rows={5}
                maxLength={4096}
                value={waMessage}
                onChange={(e) => setWaMessage(e.target.value)}
                data-testid="wa-message-input"
              />
              <p className="text-xs text-muted-foreground text-right">
                {waMessage.length}/4096 characters
              </p>
            </div>
            <div className="p-2 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 text-[11px] text-green-800">
              Message will be picked up by your logged-in Chrome extension within a few seconds and sent from your own WhatsApp number.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaDialogOpen(false)}>Cancel</Button>
            <Button onClick={sendWhatsApp} disabled={sendingWA} data-testid="send-wa-btn" className="bg-green-600 hover:bg-green-700">
              {sendingWA && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Queue Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Writer Dialog */}
      <ContentWriterDialog
        open={contentWriterOpen}
        onOpenChange={setContentWriterOpen}
        application={contentWriterApp}
        onSaved={() => { if (typeof fetchApplications === 'function') fetchApplications(); }}
      />


      <Dialog open={vettingDialogOpen} onOpenChange={setVettingDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Document Vetting Report
            </DialogTitle>
            <DialogDescription>
              AI-powered eligibility assessment for {vettingApp?.student_name}
            </DialogDescription>
          </DialogHeader>
          
          {!vettingReport ? (
            <div className="space-y-4">
              {loadingPastReports || loadingEstimate ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">{loadingPastReports ? 'Loading past reports...' : 'Calculating cost estimate...'}</span>
                </div>
              ) : (
                <>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm">Scan all uploaded documents using AI and check them against university eligibility criteria.</p>
                    <p className="text-xs text-muted-foreground mt-2">University: {vettingApp?.university_name || vettingEstimate?.university_name || 'Not selected'}</p>
                  </div>
                  
                  {/* Cost Estimate Section */}
                  {vettingEstimate && (
                    <div className="border rounded-lg p-4 space-y-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        📊 Cost Estimate
                      </h4>
                      
                      {/* Documents to analyze */}
                      <div className="bg-muted/50 rounded-lg p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Documents to Analyze: {vettingEstimate.documents_to_analyze || vettingEstimate.documents_count} of {vettingEstimate.documents_count} files ({vettingEstimate.total_size_mb} MB)
                        </p>
                      </div>
                      
                      {/* Cost breakdown */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-700">{vettingEstimate.credits_required}</p>
                          <p className="text-xs text-blue-600">AI Credit{vettingEstimate.credits_required > 1 ? 's' : ''}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-green-700">${vettingEstimate.actual_cost_usd?.toFixed(2)}</p>
                          <p className="text-xs text-green-600">Your Cost</p>
                        </div>
                      </div>
                      
                      {/* Simple pricing info */}
                      <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                        <p>💡 <strong>1 AI Credit = $0.20</strong> for all AI features</p>
                      </div>
                      
                      {/* Token estimate */}
                      <div className="flex justify-between text-xs text-muted-foreground border-t pt-2">
                        <span>Documents: {vettingEstimate.documents_to_analyze} of {vettingEstimate.documents_count}</span>
                        <span>Your Balance: {vettingEstimate.credits_available} credits</span>
                      </div>
                      
                      {/* Warnings */}
                      {vettingEstimate.warnings && vettingEstimate.warnings.length > 0 && (
                        <div className="space-y-1">
                          {vettingEstimate.warnings.map((w, i) => (
                            <p key={`warn-${i}`} className="text-xs text-amber-600">⚠️ {w}</p>
                          ))}
                        </div>
                      )}
                      
                      {/* Balance after vetting */}
                      {vettingEstimate.has_sufficient_credits && (
                        <p className="text-xs text-muted-foreground">
                          Balance after: <span className="font-medium">{vettingEstimate.credits_after_vetting} credits (${(vettingEstimate.credits_after_vetting * 0.10).toFixed(2)})</span>
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Error state */}
                  {vettingEstimate && !vettingEstimate.can_proceed && vettingEstimate.error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{vettingEstimate.error}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <CreditCostBadge feature={CREDIT_FEATURES.DOCUMENT_VETTING} />
                    <Button 
                      onClick={runDocumentVetting} 
                      disabled={isVetting || (vettingEstimate && !vettingEstimate.can_proceed)} 
                      data-testid="run-vetting-btn"
                    >
                      {isVetting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>) : (<><Sparkles className="w-4 h-4 mr-2" />Start AI Vetting</>)}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Past Reports Selector */}
              {pastVettingReports.length > 1 && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                  <span className="text-xs text-muted-foreground">Past Reports:</span>
                  <div className="flex gap-1 flex-wrap">
                    {pastVettingReports.map((report, idx) => (
                      <button
                        key={report.id}
                        onClick={() => setVettingReport(report)}
                        className={`px-2 py-1 text-xs rounded ${vettingReport?.id === report.id ? 'bg-primary text-white' : 'bg-background hover:bg-muted-foreground/10'}`}
                      >
                        {idx === 0 ? 'Latest' : new Date(report.created_at).toLocaleDateString()}
                      </button>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" className="ml-auto text-xs h-7" onClick={() => { setVettingReport(null); fetchVettingEstimate(vettingApp.id); }}>
                    <Sparkles className="w-3 h-3 mr-1" />Run New
                  </Button>
                </div>
              )}
              
              {/* Report Date */}
              {vettingReport.created_at && (
                <p className="text-xs text-muted-foreground">Report generated: {new Date(vettingReport.created_at).toLocaleString()}</p>
              )}
              
              <div className={`p-4 rounded-lg border-2 ${vettingReport.overall_status === 'ELIGIBLE' ? 'bg-green-100 border-green-500' : vettingReport.overall_status === 'NOT_ELIGIBLE' ? 'bg-red-100 border-red-500' : 'bg-amber-100 border-amber-500'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-bold text-xl ${vettingReport.overall_status === 'ELIGIBLE' ? 'text-green-800' : vettingReport.overall_status === 'NOT_ELIGIBLE' ? 'text-red-800' : 'text-amber-800'}`}>
                      {vettingReport.overall_status === 'ELIGIBLE' ? '✓ ELIGIBLE' : vettingReport.overall_status === 'NOT_ELIGIBLE' ? '✗ NOT ELIGIBLE' : '⚠ NEEDS REVIEW'}
                    </p>
                    <p className="text-sm text-gray-700 mt-1">{vettingReport.summary}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-gray-800">{vettingReport.confidence_score}%</p>
                    <p className="text-xs text-gray-600">Confidence</p>
                  </div>
                </div>
              </div>
              {vettingReport.extracted_data && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3">Extracted Information</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {vettingReport.extracted_data.cgpa?.value && (
                      <div className="p-2 bg-muted rounded"><span className="text-muted-foreground">CGPA:</span> <span className="font-semibold">{vettingReport.extracted_data.cgpa.value}</span></div>
                    )}
                    {vettingReport.extracted_data.graduation_date?.value && (
                      <div className="p-2 bg-muted rounded"><span className="text-muted-foreground">Graduation:</span> <span className="font-semibold">{vettingReport.extracted_data.graduation_date.value}</span></div>
                    )}
                    {vettingReport.extracted_data.education_gap_years != null && (
                      <div className={`p-2 rounded ${vettingReport.extracted_data.education_gap_years > 2 ? 'bg-amber-100' : 'bg-muted'}`}>
                        <span className="text-muted-foreground">Education Gap:</span> 
                        <span className={`font-semibold ${vettingReport.extracted_data.education_gap_years > 2 ? 'text-amber-700' : ''}`}> {vettingReport.extracted_data.education_gap_years} years</span>
                      </div>
                    )}
                    {vettingReport.extracted_data.english_test?.score && (
                      <div className={`p-2 rounded ${vettingReport.extracted_data.english_test.is_valid === false ? 'bg-red-100' : 'bg-muted'}`}>
                        <span className="text-muted-foreground">{vettingReport.extracted_data.english_test.type}:</span> 
                        <span className="font-semibold"> {vettingReport.extracted_data.english_test.score}</span>
                        {vettingReport.extracted_data.english_test.is_valid === false && (
                          <span className="text-red-600 text-xs ml-2">⚠️ EXPIRED</span>
                        )}
                        {vettingReport.extracted_data.english_test.test_date && (
                          <span className="text-xs text-muted-foreground ml-2">(Test: {vettingReport.extracted_data.english_test.test_date})</span>
                        )}
                      </div>
                    )}
                    {vettingReport.extracted_data.degree_obtained && (
                      <div className="p-2 bg-muted rounded col-span-2"><span className="text-muted-foreground">Degree:</span> <span className="font-semibold">{vettingReport.extracted_data.degree_obtained}</span></div>
                    )}
                    {vettingReport.extracted_data.institution && (
                      <div className="p-2 bg-muted rounded col-span-2"><span className="text-muted-foreground">Institution:</span> <span className="font-semibold">{vettingReport.extracted_data.institution}</span></div>
                    )}
                  </div>
                </div>
              )}
              {vettingReport.criteria_results && vettingReport.criteria_results.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3">Eligibility Criteria Check</h4>
                  <div className="space-y-2">
                    {vettingReport.criteria_results.map((r, i) => {
                      const wrapCls = r.status === 'PASS'
                        ? 'bg-green-50 border-green-200'
                        : r.status === 'FAIL' ? 'bg-red-50 border-red-200' : 'bg-gray-50';
                      const badgeCls = r.status === 'PASS'
                        ? 'bg-green-500'
                        : r.status === 'FAIL' ? 'bg-red-500' : 'bg-amber-500';
                      return (
                        <div key={`crit-${r.criterion_name || i}`} className={`p-3 rounded-lg border ${wrapCls}`}>
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-sm font-medium text-gray-800">{r.criterion_name}</p>
                              <p className="text-xs text-gray-600">Required: {r.criterion_value} | Found: {r.extracted_value || 'N/A'}</p>
                            </div>
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white ${badgeCls}`}>{r.status}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* IELTS/English Test Validity Warning */}
              {vettingReport.validity_checks?.english_test_warning && (
                <div className="border rounded-lg p-4 bg-red-50 border-red-200">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-red-800">
                    ⚠️ English Test Validity Issue
                  </h4>
                  <p className="text-sm text-red-700">{vettingReport.validity_checks.english_test_warning}</p>
                  {vettingReport.validity_checks.english_test_expiry && (
                    <p className="text-xs text-red-600 mt-1">Expired on: {vettingReport.validity_checks.english_test_expiry}</p>
                  )}
                </div>
              )}
              {vettingReport.gap_analysis && vettingReport.gap_analysis.has_significant_gap && (
                <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-amber-800">
                    ⚠️ Education Gap Detected
                  </h4>
                  <p className="text-sm text-amber-700">
                    Gap Duration: <strong>{vettingReport.gap_analysis.gap_duration_years} years</strong>
                    {vettingReport.gap_analysis.gap_justification_required && !vettingReport.gap_analysis.gap_justification_provided && (
                      <span className="ml-2 text-red-600 font-medium">• Gap justification required but not found</span>
                    )}
                  </p>
                </div>
              )}
              {vettingReport.recommendations && vettingReport.recommendations.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    💡 Recommendations
                  </h4>
                  <div className="space-y-2">
                    {vettingReport.recommendations.map((rec, i) => {
                      const wrapCls = rec.priority === 'HIGH'
                        ? 'bg-red-50 border-red-200'
                        : rec.priority === 'MEDIUM' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200';
                      const badgeCls = rec.priority === 'HIGH'
                        ? 'bg-red-500'
                        : rec.priority === 'MEDIUM' ? 'bg-amber-500' : 'bg-blue-500';
                      return (
                        <div key={`rec-${rec.title || i}`} className={`p-3 rounded-lg border ${wrapCls}`}>
                          <div className="flex items-start gap-2">
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-bold text-white ${badgeCls}`}>{rec.priority}</span>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{rec.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{rec.description}</p>
                              {rec.action_required && (
                                <p className="text-xs text-gray-800 mt-2 font-medium">→ Action: {rec.action_required}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="p-3 bg-muted rounded-lg text-sm flex justify-between">
                <span>Documents: {vettingReport.documents_analyzed}</span>
                <span>Time: {vettingReport.processing_time_seconds?.toFixed(1)}s</span>
                <span>Cost: ${vettingReport.estimated_cost_usd?.toFixed(4)}</span>
              </div>
              <Button onClick={() => { setVettingReport(null); setVettingDialogOpen(false); }} className="w-full">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activity Log Dialog */}
      <Dialog open={activityLogOpen} onOpenChange={setActivityLogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Activity Log
            </DialogTitle>
            <DialogDescription>
              {activityLogApp && (
                <span>Activity history for <strong>{activityLogApp.student_name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {loadingActivityLog ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : activityLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No activity logged yet</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[400px] pr-4">
              <div className="space-y-3">
                {activityLogs.map((log, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      log.type === 'email' 
                        ? log.details?.status === 'sent' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                        : 'bg-muted'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {log.type === 'email' ? (
                            <Mail className="w-4 h-4 text-blue-500" />
                          ) : (
                            <FileText className="w-4 h-4 text-slate-500" />
                          )}
                          <span className="font-medium text-sm capitalize">
                            {log.action?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        {log.type === 'email' && log.details && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            <p>To: {log.details.to}</p>
                            <p className="truncate">Subject: {log.details.subject}</p>
                          </div>
                        )}
                        {log.type === 'activity' && log.details && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {typeof log.details === 'object' 
                              ? Object.entries(log.details).map(([k, v]) => (
                                  <p key={k}><span className="capitalize">{k.replace(/_/g, ' ')}:</span> {String(v)}</p>
                                ))
                              : <p>{String(log.details)}</p>
                            }
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Desk History Dialog */}
      <Dialog open={deskHistoryOpen} onOpenChange={setDeskHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Desk Shift History
            </DialogTitle>
            <DialogDescription>
              {deskHistoryData && (
                <span>Application timeline for <strong>{deskHistoryData.student_name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {deskHistoryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : deskHistoryData ? (
            <DeskHistoryTimeline data={deskHistoryData} />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No history available
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* r66.8 — Duplicate Application Dialog */}
      <DuplicateApplicationDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        app={duplicateSource}
        api={api}
        onSuccess={() => { fetchApplications(); }}
      />
    </div>
    </>
  );
};

// Separate component to avoid Babel plugin crash
const DeskHistoryTimeline = ({ data }) => {
  const historyItems = [];
  
  for (let i = 0; i < data.history.length; i++) {
    const item = data.history[i];
    historyItems.push(
      <div key={item.id || i} className="relative pl-10">
        <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 ${
          item.is_creation 
            ? 'bg-green-500 border-green-500' 
            : 'bg-primary border-primary'
        }`} />
        
        <div className="p-3 rounded-lg border bg-card">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              {item.is_creation ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                  Created
                </Badge>
              ) : (
                <div className="flex items-center gap-1 text-sm">
                  <Badge variant="outline" className="capitalize">{item.from_desk}</Badge>
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <Badge className="capitalize">{item.to_desk}</Badge>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {item.date ? new Date(item.timestamp).toLocaleDateString() : 'N/A'}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {item.timestamp ? new Date(item.timestamp).toLocaleTimeString() : 'N/A'}
            </div>
          </div>
          
          {!item.is_creation && (
            <div className="mt-2 text-xs text-muted-foreground">
              <span>By: {item.shifted_by}</span>
              {item.notes && (
                <p className="mt-1 italic">"{item.notes}"</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Current Desk</span>
          <Badge className="capitalize">{data.current_desk}</Badge>
        </div>
      </div>
      
      <ScrollArea className="h-[300px] pr-4">
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
          <div className="space-y-4">
            {historyItems}
          </div>
        </div>
      </ScrollArea>
      
      <div className="p-3 rounded-lg bg-muted text-sm">
        <div className="flex justify-between">
          <span>Total Shifts: {data.history.filter(h => !h.is_creation).length}</span>
          <span>Created: {data.created_at ? new Date(data.created_at).toLocaleDateString() : 'N/A'}</span>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsPage;
